import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GeneratorInput, Character2DRef } from './Generator';
import { type GenerationResult, CanvasEditor } from './Slides';
import { CanvasItem } from './CanvasItem';
import { FullscreenImageViewer, ViewerImage } from './FullscreenImageViewer';
import { LookbookToolbar } from './LookbookToolbar';
import { DynamicIslandGenerator } from './DynamicIslandGenerator';
import { GeneratorSelections, Project, Generation } from '../types';
import { ProjectService } from '../lib/projectService';
import { ArrowCounterClockwiseIcon, CubeIcon, FileTextIcon, UserIcon, LayoutIcon, FilmStripIcon, PenNibIcon, FilmSlateIcon, VideoIcon, ClockIcon, ListBulletsIcon, MagnifyingGlassIcon, PlusIcon, DotsThreeIcon, DotsThreeVerticalIcon, SparkleIcon, XIcon, CheckIcon, NoteIcon, MinusIcon, CursorIcon, HandIcon, ArrowsOutIcon, FrameCornersIcon, TextTIcon } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useToast } from '../context/ToastContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { generateImageBlob, getDimensionsFor2MP } from '../lib/imageGeneration';
import { DEFAULT_IMAGE_MODEL } from '../lib/model-config';
import { uploadToR2, deleteFromR2 } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { useSendToChat } from '../hooks/useSendToChat';
import {
  Session,
  LookbookState,
  fetchSessions,
  createSession,
  updateSession,
  saveTempSession,
  loadTempSession,
  clearTempSession,
  getMostRecentSession,
  // Project-based lookbook state
  getActiveProjectId,
  setActiveProjectId,
  saveProjectLookbookState,
  loadProjectLookbookState,
  getProjectsForLookbook
} from '../lib/sessions';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useLookbookStore } from '../stores/lookbookStore';

interface CreativeConsoleProps {
  showShowcase?: boolean;
  forcedValues?: {
    prompt?: string;
    selections?: Partial<GeneratorSelections>;
    model?: string;
    characterReference?: any;  // Character2DRef for restoring character selection
  };
  onGenerationComplete?: (imageUrl: string, prompt: string) => void;
  onCanvasReady?: (api: {
    add: (imageUrl: string, prompt: string, originalId?: number | string, settings?: any, model?: string) => void;
    remove: (id: number | string) => void;
  }) => void;
  onCanvasChange?: (items: any[]) => void;
  onDelete?: () => void;
  onHasContent?: (hasContent: boolean) => void;
  onClearForcedValues?: () => void;
  // Content for RECENT and STORYBOARD tabs
  recentGenerationsContent?: React.ReactNode;
  storyboardContent?: React.ReactNode;

  // Sidebar state for accurate viewport centering
  sidebarState?: { leftOpen: boolean; rightOpen: boolean };
}



export const CreativeConsole: React.FC<CreativeConsoleProps> = ({ showShowcase = true, forcedValues: propForcedValues, onGenerationComplete, onCanvasReady, onDelete, onHasContent, onClearForcedValues, recentGenerationsContent, storyboardContent, onCanvasChange, sidebarState }) => {
  const [generations, setGenerations] = React.useState<Generation[]>(() => {
    const saved = loadTempSession();
    return saved?.generations || [];
  });

  // Zustand store for save status reporting (UI consistency with StoryPage)
  const setSaveStatus = useLookbookStore(state => state.setSaveStatus);
  const setStoreDirty = useLookbookStore(state => state.setIsDirty);

  // State Initialization (Moved up to avoid ReferenceError in helper functions)
  const [lookbookBorder, setLookbookBorder] = React.useState<'thick' | 'thin' | 'none'>('thick');
  const [canvasTool, setCanvasTool] = React.useState<'select' | 'pan' | 'text'>('select');

  // Drag & Layout State
  const [draggedId, setDraggedId] = React.useState<string | number | null>(null);
  const [selectedId, setSelectedId] = React.useState<number | string | null>(null);
  // Multi-select state for shift+click selection
  const [selectedIds, setSelectedIds] = React.useState<Set<string | number>>(new Set());
  // Marquee selection state (click+drag on empty canvas)
  const [marqueeStart, setMarqueeStart] = React.useState<{ x: number; y: number } | null>(null);
  const [marqueeEnd, setMarqueeEnd] = React.useState<{ x: number; y: number } | null>(null);
  const [fullscreenImageIndex, setFullscreenImageIndex] = React.useState<number | null>(null);

  // View state: 'lookbook' | 'gallery' | 'storyboard' | 'editor'
  const [viewMode, setViewMode] = React.useState<'lookbook' | 'gallery' | 'storyboard' | 'editor'>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('ezart_view_mode');
      if (stored === 'canvas') return 'lookbook';
      if (stored === 'gallery' || stored === 'storyboard' || stored === 'editor' || stored === 'lookbook') return stored as any;
    }
    return 'lookbook';
  });

  // Notification State
  const [notification, setNotification] = React.useState<{ message: string, type: 'success' | 'error' } | null>(null);

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Get active project from context
  const { selectedProjectId: globalProjectId, projects: globalProjects } = useProject();
  const activeProject = globalProjects.find(p => p.id === globalProjectId);

  // Check permissions for the active project
  const { canEdit, canViewPages, isLoading: permissionsLoading } = useProjectPermissions(globalProjectId);

  // Track which image URLs are in the current project (for visual feedback on folder button)
  const [projectImageUrls, setProjectImageUrls] = React.useState<Set<string>>(new Set());
  // Track in-flight operations to prevent double-clicks
  const [projectToggleInFlight, setProjectToggleInFlight] = React.useState<Set<string>>(new Set());

  // Load project images when project changes
  React.useEffect(() => {
    const loadProjectImages = async () => {
      console.log('📁 [Lookbook] loadProjectImages called, activeProject:', activeProject?.id);
      if (!activeProject) {
        setProjectImageUrls(new Set());
        return;
      }
      // Fetch scenes directly from database (project.scenes is not populated)
      const { data: scenes } = await supabase
        .from('scenes')
        .select('id')
        .eq('project_id', activeProject.id);

      const sceneIds = scenes?.map(s => s.id) || [];
      console.log('📁 [Lookbook] Scene IDs from DB:', sceneIds);
      if (sceneIds.length === 0) {
        setProjectImageUrls(new Set());
        return;
      }
      const { data: shots } = await supabase
        .from('shots')
        .select('image_url')
        .in('scene_id', sceneIds);
      console.log('📁 [Lookbook] Loaded', shots?.length, 'shots from project');
      setProjectImageUrls(new Set(shots?.map(s => s.image_url) || []));
    };
    loadProjectImages();
  }, [activeProject]);

  // Toggle add/remove from project (like favorite toggle)
  const handleAddToProject = async (img: string, prompt: string) => {
    console.log('📁 [Lookbook] handleAddToProject called:', { img: img?.slice(0, 50), activeProject: activeProject?.id });

    if (!activeProject) {
      showNotification('Select a project first', 'error');
      return;
    }

    // Prevent double-clicks
    if (projectToggleInFlight.has(img)) {
      console.log('📁 [Lookbook] Already in flight, ignoring');
      return;
    }
    setProjectToggleInFlight(prev => new Set([...prev, img]));

    try {
      // Fetch scenes directly from database (project.scenes is not populated)
      const { data: scenes } = await supabase
        .from('scenes')
        .select('id')
        .eq('project_id', activeProject.id);

      const allSceneIds = scenes?.map(s => s.id) || [];
      console.log('📁 [Lookbook] Scene IDs from DB:', allSceneIds);

      // Check if image already exists in any scene of the project
      if (allSceneIds.length > 0) {
        const { data: existingShots, error } = await supabase
          .from('shots')
          .select('id, image_url')
          .in('scene_id', allSceneIds)
          .eq('image_url', img);

        console.log('📁 [Lookbook] Existing shots:', existingShots?.length, 'error:', error);

        if (existingShots && existingShots.length > 0) {
          // Already exists - remove ALL duplicates (toggle off)
          console.log('📁 [Lookbook] REMOVING', existingShots.length, 'shots');
          for (const shot of existingShots) {
            await supabase
              .from('shots')
              .delete()
              .eq('id', shot.id);
          }
          // Update tracking state
          setProjectImageUrls(prev => {
            const next = new Set(prev);
            next.delete(img);
            return next;
          });
          showNotification('Removed from project', 'success');
          return;
        }
      }

      // Not in project - add it
      console.log('📁 [Lookbook] ADDING to project');
      let targetSceneId = allSceneIds[0];
      if (!targetSceneId) {
        const scene = await ProjectService.createScene(activeProject.id, 'Assets');
        if (scene) targetSceneId = scene.id;
      }

      if (targetSceneId) {
        await ProjectService.addShot(targetSceneId, img, prompt, prompt?.slice(0, 50));
        // Update tracking state
        setProjectImageUrls(prev => new Set([...prev, img]));
        showNotification('Added to project!', 'success');
      } else {
        showNotification('Could not add to project', 'error');
      }
    } finally {
      setProjectToggleInFlight(prev => {
        const next = new Set(prev);
        next.delete(img);
        return next;
      });
    }
  };

  const handleTogglePublic = async (id: string | number, currentStatus: boolean) => {
    if (!id) return;

    console.log('Toggle public called:', { id, currentStatus, idType: typeof id });

    // Check if this generation is still loading (not yet saved to database)
    const gen = generations.find(g => g.id === id);
    if (gen?.isLoading) {
      showNotification('Please wait until the image finishes generating', 'error');
      return;
    }

    // Convert id to string for database operations
    const idStr = String(id);

    // Check if this is a valid database ID (UUID format) or a temp/recalled ID
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idStr);
    const isTempId = typeof id === 'number' || idStr.startsWith('recalled-') || idStr.startsWith('temp-');

    console.log('ID check:', { idStr, isUUID, isTempId });

    if (isTempId && !isUUID) {
      showNotification('Please wait until the image is saved', 'error');
      return;
    }

    // Optimistic update
    setGenerations(prev => prev.map(g =>
      g.id === id ? { ...g, is_public: !currentStatus } : g
    ));

    console.log('Updating database...', { id: idStr, newValue: !currentStatus });

    const { error, data } = await supabase
      .from('generations')
      .update({ is_public: !currentStatus })
      .eq('id', idStr)
      .select();

    console.log('Database result:', { error, data });

    if (error) {
      console.error('Error toggling public status:', error, { id: idStr });
      // Revert on error
      setGenerations(prev => prev.map(g =>
        g.id === id ? { ...g, is_public: currentStatus } : g
      ));
      showNotification('Failed to update public status', 'error');
    } else {
      showNotification(!currentStatus ? 'Image is now Public' : 'Image is now Private', 'success');
    }
  };

  const handleToggleFavorite = async (id: string | number, currentStatus: boolean) => {
    if (!id || !user) return;

    // Check if this generation is still loading or has a temp ID
    const gen = generations.find(g => g.id === id);
    if (gen?.isLoading) {
      showNotification('Please wait until the image finishes generating', 'error');
      return;
    }

    // Convert id to string for database operations
    const idStr = String(id);

    // Check if this is a valid database ID (UUID format) or a temp/recalled ID
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idStr);
    const isTempId = typeof id === 'number' || idStr.startsWith('recalled-') || idStr.startsWith('temp-');

    if (isTempId && !isUUID) {
      showNotification('Please wait until the image is saved', 'error');
      return;
    }

    // Optimistic update
    setGenerations(prev => prev.map(g =>
      g.id === id ? { ...g, is_favorited: !currentStatus } : g
    ));

    if (currentStatus) {
      // Remove from favorites
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('generation_id', idStr);

      if (error) {
        console.error('Error removing favorite:', error);
        setGenerations(prev => prev.map(g =>
          g.id === id ? { ...g, is_favorited: currentStatus } : g
        ));
        showNotification('Failed to remove from favorites', 'error');
      } else {
        showNotification('Removed from favorites', 'success');
      }
    } else {
      // Add to favorites
      const { error } = await supabase
        .from('favorites')
        .insert({ user_id: user.id, generation_id: id });

      if (error) {
        console.error('Error adding favorite:', error);
        setGenerations(prev => prev.map(g =>
          g.id === id ? { ...g, is_favorited: currentStatus } : g
        ));
        showNotification('Failed to add to favorites', 'error');
      } else {
        showNotification('Added to favorites!', 'success');
      }
    }
  };

  // Weekly Challenge Logic
  const [activeChallengeId, setActiveChallengeId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchActiveChallenge = async () => {
      try {
        const { data } = await supabase
          .from('weekly_challenges')
          .select('id')
          .eq('is_active', true)
          .maybeSingle();
        if (data) setActiveChallengeId(data.id);
      } catch (e) {
        // Ignore error
      }
    };
    fetchActiveChallenge();
  }, []);

  const handleChallengeSubmission = async (id: string | number) => {
    if (!activeChallengeId || !user || !id) return;

    // Check if already submitted (optional, or rely on DB constraint)
    const { data: existing } = await supabase
      .from('challenge_submissions')
      .select('id')
      .eq('challenge_id', activeChallengeId)
      .eq('user_id', user.id)
      .single();

    if (existing) {
      showNotification('You have already submitted an entry for this challenge!', 'error');
      return;
    }

    const { error } = await supabase
      .from('challenge_submissions')
      .insert({
        challenge_id: activeChallengeId,
        user_id: user.id,
        generation_id: id
      });

    if (error) {
      console.error('Challenge submission error:', error);
      showNotification('Failed to submit entry.', 'error');
    } else {
      showNotification('Entry submitted to Weekly Challenge!', 'success');
    }
  };

  // Expose addToCanvas function to parent
  // Expose methods to parent
  const selectedIdRef = React.useRef<number | string | null>(null);
  const handleFitToViewRef = React.useRef<() => void>(() => { });
  const viewportControlRef = React.useRef<any>(null);
  // Viewport state - loaded from Supabase when project loads, no localStorage fallback
  const viewportStateRef = React.useRef<any>(null);
  const pendingCenterRef = React.useRef<{ x: number; y: number; w: number; h: number } | null>(null);


  // Helper: Get true visible viewport dimensions (accounting for sidebars)
  // This ensures items are centered in the VISIBLE area, not the window center
  const getViewportDimensions = React.useCallback(() => {
    // Use props if available, otherwise read from localStorage or default
    const leftOpen = sidebarState?.leftOpen ?? (typeof window !== 'undefined' ? localStorage.getItem('ezart_sidebar_open') !== 'false' : true);
    const rightOpen = sidebarState?.rightOpen ?? (typeof window !== 'undefined' ? localStorage.getItem('ezart_rightpanel_open') === 'true' : false);

    const SIDEBAR_WIDTH = 240;
    const RIGHT_PANEL_WIDTH = 640;

    const leftOffset = leftOpen ? SIDEBAR_WIDTH : 0;
    const rightOffset = rightOpen ? RIGHT_PANEL_WIDTH : 0;

    const windowW = window.innerWidth;
    const windowH = window.innerHeight;

    // Visible width is reduced by sidebars (Container Width)
    // StudioLayout uses padding to squeeze the main content area, so 0,0 is at the top-left of the visible area
    const visibleWidth = windowW - leftOffset - rightOffset;
    const visibleHeight = windowH;

    // The center of the visible area (Local Coordinates)
    const centerX = visibleWidth / 2;
    const centerY = visibleHeight / 2;

    return {
      width: visibleWidth,
      height: visibleHeight,
      centerX,
      centerY,
      leftOffset,
      rightOffset
    };
  }, [sidebarState?.leftOpen, sidebarState?.rightOpen]);

  // Helper: Center viewport on specific coordinates
  // fitToView: calculate optimal scale so image occupies ~50% of viewport
  const centerViewportOnItem = React.useCallback((x: number, y: number, w: number, h: number, fitToView = false) => {
    if (viewportControlRef.current?.setTransform) {
      // Delay slightly to ensure layout is ready if called during mount
      setTimeout(() => {
        const viewport = getViewportDimensions();
        const containerW = viewport.width;
        const containerH = viewport.height;

        // Calculate target scale
        let targetScale = viewportStateRef.current?.scale ?? 1;

        if (fitToView) {
          // Calculate scale so image occupies ~50% of viewport
          const targetFill = 0.5;
          const scaleToFitW = (containerW * targetFill) / w;
          const scaleToFitH = (containerH * targetFill) / h;
          // Use the smaller scale to ensure it fits in both dimensions
          const fitScale = Math.min(scaleToFitW, scaleToFitH);
          // Clamp to reasonable bounds (0.3 to 2.0)
          targetScale = Math.max(0.3, Math.min(2.0, fitScale));
        }

        // Canvas Center Item
        const itemCenterX = x + w / 2;
        const itemCenterY = y + h / 2;

        // Calculate Position
        // pos = (ViewCenter) - (ItemCenter * Scale)
        const newX = (containerW / 2) - (itemCenterX * targetScale);
        const newY = (containerH / 2) - (itemCenterY * targetScale);

        viewportControlRef.current.setTransform(newX, newY, targetScale, 500, 'easeOut');

        // Also update state ref immediately so it persists if tab switched during anim
        viewportStateRef.current = { positionX: newX, positionY: newY, scale: targetScale };
      }, 50);
    }
  }, []);

  // Helper: Simple collision check for placement
  const checkCollision = (x: number, y: number, w: number, h: number, items: any[]) => {
    // Dynamic buffer for border merge (like CanvasItem)
    const BUFFER = lookbookBorder === 'thick' ? 6 : (lookbookBorder === 'thin' ? 2 : 0);
    const EPSILON = 0.05;

    for (const item of items) {
      const ox = item.x ?? 1500;
      const oy = item.y ?? 1500;
      const ow = item.width ?? 300;
      const oh = item.height ?? 300;

      // Calculate overlap depth
      const xOverlap = Math.min(x + w, ox + ow) - Math.max(x, ox);
      const yOverlap = Math.min(y + h, oy + oh) - Math.max(y, oy);

      // Collision if overlap > BUFFER in both axes
      if (xOverlap > BUFFER + EPSILON && yOverlap > BUFFER + EPSILON) {
        return true;
      }
    }
    return false;
  };

  const findSmartPosition = (startX: number, startY: number, w: number, h: number, items: any[]) => {
    // Masonry/Collage placement algorithm
    // Goals: 1) Fill L-shapes, 2) Prefer horizontal over vertical, 3) Create tight collages
    const PADDING = -(lookbookBorder === 'thick' ? 6 : (lookbookBorder === 'thin' ? 2 : 0));

    // If no items exist, place at viewport center
    if (items.length === 0) {
      return { x: startX, y: startY };
    }

    // Count how many edges of a position touch existing items (higher = better collage)
    const countTouchingEdges = (x: number, y: number, width: number, height: number) => {
      let touches = 0;
      const tolerance = Math.abs(PADDING) + 5;

      for (const item of items) {
        const ox = item.x ?? 1500;
        const oy = item.y ?? 1500;
        const ow = item.width ?? 300;
        const oh = item.height ?? 300;

        // Check if right edge of new touches left edge of existing
        if (Math.abs((x + width) - ox) < tolerance) {
          const overlapY = Math.min(y + height, oy + oh) - Math.max(y, oy);
          if (overlapY > 20) touches++;
        }
        // Check if left edge of new touches right edge of existing
        if (Math.abs(x - (ox + ow)) < tolerance) {
          const overlapY = Math.min(y + height, oy + oh) - Math.max(y, oy);
          if (overlapY > 20) touches++;
        }
        // Check if bottom edge of new touches top edge of existing
        if (Math.abs((y + height) - oy) < tolerance) {
          const overlapX = Math.min(x + width, ox + ow) - Math.max(x, ox);
          if (overlapX > 20) touches++;
        }
        // Check if top edge of new touches bottom edge of existing
        if (Math.abs(y - (oy + oh)) < tolerance) {
          const overlapX = Math.min(x + width, ox + ow) - Math.max(x, ox);
          if (overlapX > 20) touches++;
        }
      }
      return touches;
    };

    // Generate all possible snap positions from all items
    const allCandidates: { x: number; y: number; score: number }[] = [];

    for (const item of items) {
      const ox = item.x ?? 1500;
      const oy = item.y ?? 1500;
      const ow = item.width ?? 300;
      const oh = item.height ?? 300;

      // Basic 4-side snaps
      const positions = [
        { x: ox + ow + PADDING, y: oy },           // Right, aligned top
        { x: ox + ow + PADDING, y: oy + oh - h },  // Right, aligned bottom
        { x: ox - w - PADDING, y: oy },            // Left, aligned top
        { x: ox - w - PADDING, y: oy + oh - h },   // Left, aligned bottom
        { x: ox, y: oy + oh + PADDING },           // Bottom, aligned left
        { x: ox + ow - w, y: oy + oh + PADDING },  // Bottom, aligned right
        { x: ox, y: oy - h - PADDING },            // Top, aligned left
        { x: ox + ow - w, y: oy - h - PADDING },   // Top, aligned right
      ];

      // Add corner-fill positions (L-shape fills)
      // Check if there's another item that creates an L-shape opportunity
      for (const other of items) {
        if (other === item) continue;
        const otherX = other.x ?? 1500;
        const otherY = other.y ?? 1500;
        const otherW = other.width ?? 300;
        const otherH = other.height ?? 300;

        // Check for L-shape: item is horizontal, other is below-right corner
        if (Math.abs((ox + ow) - otherX) < 10 && Math.abs((oy + oh) - otherY) < 10) {
          // Fill the corner gap
          positions.push({ x: ox + ow + PADDING, y: oy + oh + PADDING });
        }
        // L-shape: item is horizontal, other is below-left corner
        if (Math.abs(ox - (otherX + otherW)) < 10 && Math.abs((oy + oh) - otherY) < 10) {
          positions.push({ x: ox - w - PADDING, y: oy + oh + PADDING });
        }
      }

      for (const pos of positions) {
        if (!checkCollision(pos.x, pos.y, w, h, items)) {
          const touches = countTouchingEdges(pos.x, pos.y, w, h);
          const distFromStart = Math.sqrt(Math.pow(pos.x - startX, 2) + Math.pow(pos.y - startY, 2));
          // Score: prioritize more touches, then prefer closer to viewport center
          // Also slightly prefer horizontal expansion (lower y difference)
          const yPenalty = Math.abs(pos.y - startY) * 0.5; // Penalize vertical drift
          const score = touches * 1000 - distFromStart - yPenalty;
          allCandidates.push({ ...pos, score });
        }
      }
    }

    // Sort by score (highest first)
    allCandidates.sort((a, b) => b.score - a.score);

    // Return the best candidate if any
    if (allCandidates.length > 0) {
      return { x: allCandidates[0].x, y: allCandidates[0].y };
    }

    // Fallback: original position if valid
    if (!checkCollision(startX, startY, w, h, items)) {
      return { x: startX, y: startY };
    }

    // Ultimate fallback: spiral search
    let radius = 50;
    while (radius < 2000) {
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const checkX = startX + Math.cos(angle) * radius;
        const checkY = startY + Math.sin(angle) * radius;
        if (!checkCollision(checkX, checkY, w, h, items)) {
          return { x: checkX, y: checkY };
        }
      }
      radius += 100;
    }

    return { x: startX, y: startY };
  };

  const addToCanvas = React.useCallback((imageUrl: string, prompt: string, originalId?: number | string, settings?: any, model?: string) => {
    const newId = originalId || `recalled-${Date.now()}`;

    // Helper to add item to canvas
    const addItem = (w: number, h: number) => {
      // Find position near existing items (closest to viewport center)
      let startX = 1500;
      let startY = 1500;

      if (viewportStateRef.current) {
        const { positionX, positionY, scale } = viewportStateRef.current;
        // Use sidebar-aware dimensions for placement
        const viewport = getViewportDimensions();
        startX = (viewport.centerX - positionX) / scale - (w / 2);
        startY = (viewport.centerY - positionY) / scale - (h / 2);
      }

      // Smart Collage Placement - attach to closest existing item
      const { x: finalX, y: finalY } = findSmartPosition(startX, startY, w, h, generations);

      // Store for centering after state update
      pendingCenterRef.current = { x: finalX, y: finalY, w, h };

      setGenerations(prev => {
        const filtered = prev.filter(g => g.id !== newId);
        return [...filtered, {
          id: newId,
          type: 'Recalled',
          prompt: prompt,
          img: imageUrl,
          model: model || DEFAULT_IMAGE_MODEL,
          settings: { ...settings, aspect_ratio: settings?.aspect_ratio },
          x: finalX,
          y: finalY,
          width: w,
          height: h
        }];
      });

      // Auto-Select the new item
      setSelectedId(newId);

      // Center viewport on new item after state update
      setTimeout(() => {
        if (pendingCenterRef.current) {
          const { x, y, w: pw, h: ph } = pendingCenterRef.current;
          centerViewportOnItem(x, y, pw, ph, true);
          pendingCenterRef.current = null;
        }
      }, 100);
    };

    // Load image to get true dimensions
    const img = new Image();
    // Note: Don't set crossOrigin - R2 doesn't have CORS headers configured,
    // but we can still read naturalWidth/naturalHeight without CORS

    img.onload = () => {
      const naturalW = img.naturalWidth;
      const naturalH = img.naturalHeight;

      // If dimensions are invalid, use 300x300 fallback (CanvasItem will auto-resize on load)
      if (!naturalW || !naturalH || naturalW === 0 || naturalH === 0) {
        addItem(300, 300);
        return;
      }

      // Use true natural dimensions
      addItem(naturalW, naturalH);
    };

    img.onerror = () => {
      // Add with 300x300 fallback - CanvasItem will auto-resize when image actually loads
      addItem(300, 300);
    };

    img.src = imageUrl;

  }, [generations, centerViewportOnItem, lookbookBorder, getViewportDimensions]);

  const placeTextItem = React.useCallback((x: number, y: number) => {
    const id = `text-${Date.now()}`;
    setGenerations(prev => {
      return [...prev, {
        id,
        type: 'text',
        prompt: 'Text Bubble',
        img: '',
        x: x - 150, // Center horizontally
        y: y - 100, // Center vertically
        width: 300,
        height: 200,
        bubble_style: 'speech',
        text_content: 'Type something...',
        font_family: 'Inter, sans-serif',
        text_color: '#0C0C0C',
        bubble_color: '#ffffff',
        text_align: 'center',
        created_at: new Date().toISOString()
      }];
    });
    setSelectedId(id);
    setCanvasTool('select'); // Switch back to select after placement
  }, []);

  const removeFromCanvas = React.useCallback((id: number | string) => {
    setGenerations(prev => prev.filter(g => g.id !== id));
    setLayoutRows(prev => prev.map(row => {
      return row.map(item => {
        if (item.type === 'single') {
          return item.id === id ? null : item;
        } else {
          const newIds = item.ids.filter(sid => sid !== id);
          if (newIds.length === 0) return null;
          if (newIds.length === 1) return { type: 'single', id: newIds[0] } as LayoutItem;
          return { ...item, ids: newIds, stackFlex: undefined };
        }
      }).filter(Boolean) as LayoutItem[];
    }).filter(row => row.length > 0));
  }, []);

  React.useEffect(() => {
    onCanvasReady?.({ add: addToCanvas, remove: removeFromCanvas });
  }, [onCanvasReady, addToCanvas, removeFromCanvas]);

  React.useEffect(() => {
    onCanvasChange?.(generations);

    // Coordinate Migration: Ensure every item has stable x,y in state
    // This prevents jumps when indices change or when selecting items
    setGenerations(prev => {
      let changed = false;
      const next = prev.map((g, i) => {
        const hasX = (g as any).x !== undefined;
        const hasY = (g as any).y !== undefined;
        if (!hasX || !hasY) {
          changed = true;
          return {
            ...g,
            x: (g as any).x ?? (1500 + (i % 5) * 400),
            y: (g as any).y ?? (1500 + Math.floor(i / 5) * 400),
            width: g.width ?? 300,
            height: g.height ?? 300
          };
        }
        return g;
      });
      return changed ? next : prev;
    });
  }, [generations, onCanvasChange]);

  const handleImageUpdate = React.useCallback((id: string | number, w: number, h: number) => {
    setGenerations(prev => prev.map(g => {
      if (g.id === id && !g.settings?.aspect_ratio) {
        return { ...g, settings: { ...g.settings, aspect_ratio: `${w}:${h}` } };
      }
      return g;
    }));
  }, []);

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [copiedPrompt, setCopiedPrompt] = React.useState<string | undefined>(undefined);
  const [copiedSelections, setCopiedSelections] = React.useState<Record<string, any> | undefined>(undefined);
  const [copiedModel, setCopiedModel] = React.useState<string | undefined>(undefined);

  // Drag & Layout State (Moved to top)

  // Keep ref in sync with state
  React.useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const [explicitClear, setExplicitClear] = React.useState(false);
  const [activeTool, setActiveTool] = React.useState('prompt');
  const [toolState, setToolState] = React.useState({ hasPrompt: false, hasFilters: false, hasImages: false });

  // Dynamic Island Generator State
  const [isGeneratorIslandOpen, setIsGeneratorIslandOpen] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('ezart_generator_island_open');
      return stored === null ? true : stored === 'true';
    }
    return true;
  });

  // Persist generator island state
  React.useEffect(() => {
    localStorage.setItem('ezart_generator_island_open', String(isGeneratorIslandOpen));
  }, [isGeneratorIslandOpen]);
  const [middlePanning, setMiddlePanning] = React.useState(false);
  const lastMousePos = React.useRef({ x: 0, y: 0 });
  const [activeEditorImage, setActiveEditorImage] = React.useState<string | null>(null);
  // canvasTool state moved to top
  // lookbookBorder state moved to top
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const transformComponentRef = React.useRef<any>(null);
  const [activeDropZone, setActiveDropZone] = React.useState<{ rowIdx: number, colIdx: number, position: 'left' | 'right' | 'top' | 'bottom' | 'swap' | 'new-row-top' | 'new-row-bottom' } | null>(null);
  // ViewMode state moved to top

  // border migration logic
  const prevPaddingRef = React.useRef(lookbookBorder === 'thick' ? 6 : (lookbookBorder === 'thin' ? 2 : 0));

  React.useEffect(() => {
    const newPadding = lookbookBorder === 'thick' ? 6 : (lookbookBorder === 'thin' ? 2 : 0);
    const oldPadding = prevPaddingRef.current;
    if (newPadding === oldPadding) return;
    prevPaddingRef.current = newPadding;

    setGenerations(prev => {
      const next = [...prev];
      // Run multiple passes to propagate the shift through snapped chains
      for (let pass = 0; pass < 3; pass++) {
        for (let i = 0; i < next.length; i++) {
          for (let j = 0; j < next.length; j++) {
            if (i === j) continue;
            const a = next[i];
            const b = next[j];

            // If they were snapped on X (A's left to B's right)
            if (Math.abs(a.x - (b.x + b.width - oldPadding)) < 1.5) {
              a.x = b.x + b.width - newPadding;
            }
            // If they were snapped on X (A's right to B's left)
            else if (Math.abs((a.x + a.width - oldPadding) - b.x) < 1.5) {
              a.x = b.x - a.width + newPadding;
            }

            // If they were snapped on Y (A's top to B's bottom)
            if (Math.abs(a.y - (b.y + b.height - oldPadding)) < 1.5) {
              a.y = b.y + b.height - newPadding;
            }
            // If they were snapped on Y (A's bottom to B's top)
            else if (Math.abs((a.y + a.height - oldPadding) - b.y) < 1.5) {
              a.y = b.y - a.height + newPadding;
            }
          }
        }
      }
      return next;
    });
  }, [lookbookBorder]);

  React.useEffect(() => {
    localStorage.setItem('ezart_view_mode', viewMode);
  }, [viewMode]);

  // Session State (Legacy - kept for backwards compatibility)
  const [sessions, setSessions] = React.useState<Session[]>([]);
  const [currentSession, setCurrentSession] = React.useState<Session | null>(null);

  // Project-based Lookbook State - Use shared ProjectContext for synchronization
  const { selectedProjectId: sharedProjectId, setSelectedProjectId: setSharedProjectId } = useProject();
  const [activeProjectId, setActiveProjectIdState] = React.useState<string | null>(() => sharedProjectId || getActiveProjectId());
  const [availableProjects, setAvailableProjects] = React.useState<{ id: string; title: string; lookbook_state: LookbookState | null }[]>([]);
  // Start loading as true if there's a project to load on mount
  const [isLoadingProject, setIsLoadingProject] = React.useState(() => !!(sharedProjectId || getActiveProjectId()));
  const [showProjectSelector, setShowProjectSelector] = React.useState(false);
  const isSwitchingProjectRef = React.useRef(false); // Prevent auto-save during project switch
  const pendingSyncProjectRef = React.useRef<string | null>(null); // Track pending project sync from context

  const { user } = useAuth();

  // Load available projects for lookbook on mount
  React.useEffect(() => {
    if (user) {
      getProjectsForLookbook().then(setAvailableProjects);
    }
  }, [user]);

  // Load lookbook state from active project on INITIAL MOUNT only
  // (Project switching is handled directly in handleSelectProject)
  const initialLoadDoneRef = React.useRef(false);
  React.useEffect(() => {
    const loadFromProject = async () => {
      // Only run once on mount, and only if there's a saved project ID
      if (initialLoadDoneRef.current) return;

      // If no project or no user, clear loading state and exit
      if (!activeProjectId || !user) {
        setIsLoadingProject(false);
        return;
      }

      initialLoadDoneRef.current = true;
      setIsLoadingProject(true);
      isSwitchingProjectRef.current = true; // Prevent auto-save during initial load
      try {
        // FIRST: Check for pending viewport save from previous session (browser close/restart)
        // This ensures we use the most recent viewport state, not stale Supabase data
        let pendingViewport: any = null;
        try {
          const pendingData = localStorage.getItem('ezart_viewport_pending_save');
          if (pendingData) {
            const { projectId, state: pendingState } = JSON.parse(pendingData);
            if (projectId === activeProjectId && pendingState?.viewport) {
              pendingViewport = pendingState.viewport;
              console.log('📍 Found pending viewport save from previous session:', pendingViewport);
              // Save it to Supabase in background (don't await)
              saveProjectLookbookState(activeProjectId, pendingState).then(() => {
                console.log('✅ Saved pending viewport to Supabase');
              }).catch(e => console.error('Failed to save pending viewport:', e));
            }
            localStorage.removeItem('ezart_viewport_pending_save');
          }
        } catch (e) {
          localStorage.removeItem('ezart_viewport_pending_save');
        }

        const state = await loadProjectLookbookState(activeProjectId);
        if (state && state.generations && state.generations.length > 0) {
          // Restore generations with positions
          isRestoringRef.current = true;
          setGenerations(state.generations);
          // Use pending viewport if available, otherwise use saved viewport from Supabase
          const viewportToRestore = pendingViewport || state.viewport;
          if (viewportToRestore) {
            viewportStateRef.current = viewportToRestore;
            // Apply viewport after a short delay to ensure canvas is ready
            setTimeout(() => {
              if (viewportControlRef.current?.setTransform && viewportToRestore) {
                const vp = viewportToRestore as any;
                viewportControlRef.current.setTransform(
                  vp.positionX ?? vp.x ?? 0,
                  vp.positionY ?? vp.y ?? 0,
                  vp.scale ?? 1,
                  0
                );
              }
            }, 100);
          } else {
            // No saved viewport but has content - fit to view after canvas ready
            setTimeout(() => handleFitToViewRef.current(), 150);
          }
        } else {
          // No saved state - fit to view when canvas is ready
          setTimeout(() => handleFitToViewRef.current(), 150);
        }
      } catch (e) {
        console.error('Failed to load lookbook state from project:', e);
        // On error, fit to view as fallback
        setTimeout(() => handleFitToViewRef.current(), 150);
      } finally {
        setIsLoadingProject(false);
        setTimeout(() => {
          isSwitchingProjectRef.current = false;
        }, 500);
      }
    };

    loadFromProject();
  }, [activeProjectId, user]);

  // Handle project selection
  const handleSelectProject = React.useCallback(async (projectId: string | null) => {
    // Prevent auto-save during switch
    isSwitchingProjectRef.current = true;

    // Save current state to old project before switching (if we have content)
    if (activeProjectId && generations.length > 0) {
      await saveProjectLookbookState(activeProjectId, {
        layout_data: [],
        image_ids: generations.map(g => g.id),
        generations: generations,
        viewport: viewportStateRef.current
      });
    }

    // Clear the canvas FIRST before switching
    setGenerations([]);
    viewportStateRef.current = null;

    // Close the selector
    setShowProjectSelector(false);

    // If clearing project selection, we're done
    if (!projectId) {
      setActiveProjectIdState(null);
      setActiveProjectId(null);
      // Also update shared context
      setSharedProjectId(null);
      isSwitchingProjectRef.current = false;
      return;
    }

    // Load new project's state
    setIsLoadingProject(true);
    try {
      const state = await loadProjectLookbookState(projectId);

      // Now set the active project (local and shared)
      setActiveProjectIdState(projectId);
      setActiveProjectId(projectId);
      // Update shared context to sync with other pages
      setSharedProjectId(projectId);

      if (state && state.generations && state.generations.length > 0) {
        // Restore generations with positions
        isRestoringRef.current = true;
        setGenerations(state.generations);

        // Restore viewport if available
        if (state.viewport) {
          viewportStateRef.current = state.viewport;
          // Apply viewport after a short delay to ensure canvas is ready
          setTimeout(() => {
            if (viewportControlRef.current?.setTransform && state.viewport) {
              const vp = state.viewport as any;
              viewportControlRef.current.setTransform(
                vp.positionX ?? vp.x ?? 0,
                vp.positionY ?? vp.y ?? 0,
                vp.scale ?? 1,
                0
              );
            }
          }, 100);
        }
      }
      // If no saved state, canvas stays empty (clean slate)
    } catch (e) {
      console.error('Failed to load lookbook state from project:', e);
    } finally {
      setIsLoadingProject(false);
      // Re-enable auto-save after a delay to let state settle
      setTimeout(() => {
        isSwitchingProjectRef.current = false;
      }, 500);
    }
  }, [activeProjectId, generations, setSharedProjectId]);

  // Sync with shared project context - when shared context changes, update local state
  // This effect MUST be after handleSelectProject is defined to avoid stale closure
  React.useEffect(() => {
    // Skip if we're currently switching projects (prevents infinite loop)
    if (isSwitchingProjectRef.current) return;

    // Only sync if the shared context differs from our local state
    if (sharedProjectId !== activeProjectId) {
      // Shared project changed externally (from another page like CreativeSidebar)
      if (sharedProjectId) {
        handleSelectProject(sharedProjectId);
      } else if (activeProjectId) {
        // Project was cleared from shared context
        handleSelectProject(null);
      }
    }
    // Note: handleSelectProject intentionally excluded from deps to prevent infinite loop
    // The ref check above ensures we don't miss legitimate external changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedProjectId, activeProjectId]);

  // Load temp session on mount (for refresh persistence) - LEGACY, now uses project-based
  React.useEffect(() => {
    // Only use temp session if no active project is set
    if (!activeProjectId && generations.length === 0) {
      const saved = loadTempSession();
      if (saved?.generations && saved.generations.length > 0) {
        setGenerations(saved.generations);
        if (saved.viewport) {
          viewportStateRef.current = saved.viewport;
        }
      }
    }
  }, []);

  // Save to temp session AND active project on change (throttled)
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  React.useEffect(() => {
    // Skip auto-save during project switch
    if (isSwitchingProjectRef.current) return;

    if (generations.length > 0) {
      // Save to temp session for refresh persistence (viewport saved to Supabase separately)
      saveTempSession([], generations.map(g => g.id), generations);

      // Throttled save to project (if active)
      if (activeProjectId && user) {
        // Mark as dirty in store (for UI status)
        setStoreDirty(true);

        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(async () => {
          // Double-check we're not switching when timeout fires
          if (!isSwitchingProjectRef.current) {
            setSaveStatus('saving');
            try {
              await saveProjectLookbookState(activeProjectId, {
                layout_data: [],
                image_ids: generations.map(g => g.id),
                generations: generations,
                viewport: viewportStateRef.current
              });
              setSaveStatus('saved');
              setStoreDirty(false);
              // Reset to idle after 2 seconds
              setTimeout(() => setSaveStatus('idle'), 2000);
            } catch (error) {
              console.error('Failed to save lookbook state:', error);
              setSaveStatus('error');
            }
          }
        }, 2000); // 2 second debounce
      }
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [generations, activeProjectId, user, setSaveStatus, setStoreDirty]);

  // Legacy: Auto-save to DB sessions (kept for backwards compatibility)
  React.useEffect(() => {
    if (!currentSession || !user) return;

    const timeout = setTimeout(() => {
      updateSession(currentSession.id, {
        name: currentSession.name,
        layout_data: [], // Legacy field
        image_ids: generations.map(g => g.id),
        viewport: viewportStateRef.current // Save viewport to DB
      });
    }, 2000);

    return () => clearTimeout(timeout);
  }, [generations, currentSession, user]);

  // Viewport version counter to trigger Supabase saves on zoom/pan changes
  const [viewportVersion, setViewportVersion] = React.useState(0);
  const viewportSaveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounced viewport save to Supabase (separate from generations save)
  React.useEffect(() => {
    // Skip initial render and project switches
    if (viewportVersion === 0 || isSwitchingProjectRef.current) return;
    if (!activeProjectId || !user) return;

    // Mark as dirty
    setStoreDirty(true);

    // Clear any pending save
    if (viewportSaveTimeoutRef.current) {
      clearTimeout(viewportSaveTimeoutRef.current);
    }

    // Debounced save - wait 1 second after last viewport change
    viewportSaveTimeoutRef.current = setTimeout(async () => {
      if (!isSwitchingProjectRef.current && viewportStateRef.current) {
        setSaveStatus('saving');
        try {
          await saveProjectLookbookState(activeProjectId, {
            layout_data: [],
            image_ids: generations.map(g => g.id),
            generations: generations,
            viewport: viewportStateRef.current
          });
          setSaveStatus('saved');
          setStoreDirty(false);
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
          console.error('Failed to save viewport:', error);
          setSaveStatus('error');
        }
      }
    }, 1000);

    return () => {
      if (viewportSaveTimeoutRef.current) {
        clearTimeout(viewportSaveTimeoutRef.current);
      }
    };
  }, [viewportVersion, activeProjectId, user, generations, setSaveStatus, setStoreDirty]);

  // Periodic auto-save interval for viewport state (every 30 seconds)
  // This ensures viewport is saved even during long idle sessions
  const periodicSaveIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastSavedViewportRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!activeProjectId || !user) {
      // Clear interval if no project
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
        periodicSaveIntervalRef.current = null;
      }
      return;
    }

    // Set up periodic save every 30 seconds
    periodicSaveIntervalRef.current = setInterval(async () => {
      if (isSwitchingProjectRef.current || !viewportStateRef.current) return;

      // Only save if viewport has changed since last periodic save
      const currentViewportStr = JSON.stringify(viewportStateRef.current);
      if (currentViewportStr === lastSavedViewportRef.current) return;

      console.log('⏰ Periodic viewport save:', viewportStateRef.current);
      lastSavedViewportRef.current = currentViewportStr;

      await saveProjectLookbookState(activeProjectId, {
        layout_data: [],
        image_ids: generations.map(g => g.id),
        generations: generations,
        viewport: viewportStateRef.current
      });
    }, 30000); // 30 seconds

    return () => {
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
        periodicSaveIntervalRef.current = null;
      }
    };
  }, [activeProjectId, user, generations]);

  // Save viewport state on page unload/close
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      if (activeProjectId && viewportStateRef.current && !isSwitchingProjectRef.current) {
        // Use sendBeacon for reliable save on page close
        const payload = JSON.stringify({
          projectId: activeProjectId,
          state: {
            layout_data: [],
            image_ids: generations.map(g => g.id),
            generations: generations,
            viewport: viewportStateRef.current
          }
        });
        // Store in localStorage as fallback since sendBeacon to Supabase is complex
        try {
          localStorage.setItem('ezart_viewport_pending_save', payload);
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeProjectId, generations]);

  // NOTE: Pending viewport save is now handled in loadFromProject above
  // This ensures the pending viewport is used BEFORE loading stale data from Supabase

  // Note: Auto-save to localStorage is defined after layoutRows state

  // Session handlers
  const handleSessionSelect = async (session: Session | null) => {
    if (!session) {
      setCurrentSession(null);
      return;
    }

    setCurrentSession(session);

    // Load the session's images from database
    if (session.image_ids.length > 0) {
      const { data: genData } = await supabase
        .from('generations')
        .select('*')
        .in('id', session.image_ids);

      if (genData) {
        const loadedGens = genData.map(g => ({
          id: g.id,
          type: 'Loaded',
          prompt: g.prompt || '',
          img: g.image_url,
          model: g.model,
          settings: { aspect_ratio: g.aspect_ratio }
        }));
        // Set layout FIRST to prevent auto-layout from recalculating
        isRestoringRef.current = true;
        setLayoutRows(session.layout_data || []);
        setGenerations(loadedGens);
      }
    }
  };

  const handleSessionCreate = async (name: string) => {
    const imageIds = generations.map(g => String(g.id));
    const newSession = await createSession({
      name,
      layout_data: layoutRows,
      image_ids: imageIds
    });

    if (newSession) {
      setSessions(prev => [newSession, ...prev]);
      setCurrentSession(newSession);
    }
  };

  const handleSessionSave = async () => {
    const imageIds = generations.map(g => String(g.id));

    if (currentSession) {
      // Update existing session
      const updated = await updateSession(currentSession.id, {
        layout_data: layoutRows,
        image_ids: imageIds
      });
      if (updated) {
        setSessions(prev => prev.map(s => s.id === updated.id ? updated : s));
        setCurrentSession(updated);
      }
    }
    // If no current session, the Generator component will handle showing the modal
  };

  // Called from Generator when user enters name and confirms
  const handleSessionSaveWithName = async (name: string) => {
    await handleSessionCreate(name);
  };

  const handleSessionDelete = async (sessionId: string) => {
    const { deleteSession } = await import('../lib/sessions');
    const success = await deleteSession(sessionId);
    if (success) {
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      // Clear current session if it was deleted
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
      }
    }
  };


  React.useEffect(() => {
    if (generations.length === 0) {
      setCopiedPrompt(undefined);
      setCopiedSelections(undefined);
      setCopiedModel(undefined);
    }
    onHasContent?.(generations.length > 0);
  }, [generations.length, onHasContent]);

  // Cleanup stuck loading items on mount (failed generations from previous sessions)
  React.useEffect(() => {
    setGenerations(prev => {
      const stuckItems = prev.filter(g => g.isLoading && (!g.img || g.img === ''));
      if (stuckItems.length > 0) {
        console.log(`🧹 Cleaning up ${stuckItems.length} stuck loading item(s)`);
        return prev.filter(g => !(g.isLoading && (!g.img || g.img === '')));
      }
      return prev;
    });
  }, []);

  const handleGenerate = async (prompt: string, modelId?: string, options?: { aspectRatio?: string, inputImage?: string, referenceImages?: string[], selections?: Record<string, string | undefined>, resolution?: string, characterReference?: any, character2DRef?: Character2DRef }, originalPrompt?: string) => {
    if (!user) return;
    setIsGenerating(true);
    setExplicitClear(false);

    const model = modelId || DEFAULT_IMAGE_MODEL;
    const displayPrompt = originalPrompt || prompt;

    // Define tempId outside try so it's accessible in catch for cleanup
    let tempId: number | null = null;

    try {
      const { generateImageBlob } = await import('../lib/imageGeneration');

      // --- 1. OPTIMISTIC UI START ---
      tempId = Date.now();

      // Calculate TRUE Aspect Ratio Dimensions from the generation settings
      // Use the actual resolution dimensions instead of scaled-down placeholders
      const ratioStr = options?.aspectRatio || "1:1";
      const resolution = options?.resolution || 'High Quality';

      // Get true dimensions based on model type
      let trueDimensions: { width: number; height: number };
      if (model.includes('z-image')) {
        // Z-Image uses exact recommended dimensions
        trueDimensions = getDimensionsFor2MP(ratioStr, resolution);
      } else {
        // Nano Banana and other models - calculate from aspect ratio
        const [rw, rh] = ratioStr.split(':').map(Number);
        const ratio = (rw && rh) ? (rw / rh) : 1;
        // Use resolution-based sizing for Nano models
        const baseSize = resolution === '4K' ? 2048 : (resolution === '2K' ? 1440 : 1024);
        trueDimensions = ratio >= 1
          ? { width: baseSize, height: Math.round(baseSize / ratio) }
          : { width: Math.round(baseSize * ratio), height: baseSize };
      }

      // Smart Placement - use same logic as addToCanvas (findSmartPosition)
      const finalW = trueDimensions.width;
      const finalH = trueDimensions.height;

      // Calculate starting position from viewport center
      let startX = 1500;
      let startY = 1500;

      if (viewportStateRef.current) {
        const { positionX, positionY, scale } = viewportStateRef.current;
        // Use sidebar-aware viewport dimensions
        const viewport = getViewportDimensions();
        startX = (viewport.centerX - positionX) / scale - (finalW / 2);
        startY = (viewport.centerY - positionY) / scale - (finalH / 2);
      }

      setGenerations(prev => {
        // Use findSmartPosition for consistent magnetic attachment behavior
        const { x: bestX, y: bestY } = findSmartPosition(startX, startY, finalW, finalH, prev);

        // Store position for centering after state update
        pendingCenterRef.current = { x: bestX, y: bestY, w: finalW, h: finalH };

        return [...prev, {
          id: tempId,
          uiKey: String(tempId),
          type: 'Generated',
          prompt: displayPrompt,
          img: '', // Empty initially
          isLoading: true,
          model: model,
          x: bestX,
          y: bestY,
          width: finalW,
          height: finalH,
          settings: {
            aspect_ratio: options?.aspectRatio,
            selections: options?.selections,
            characterReference: options?.character2DRef // Store full Character2DRef for restoration
          }
        }];
      });

      // Center viewport on new image after state update
      setTimeout(() => {
        if (pendingCenterRef.current) {
          const { x, y, w, h } = pendingCenterRef.current;
          centerViewportOnItem(x, y, w, h);
          pendingCenterRef.current = null;
        }
      }, 100);

      // --- GENERATE ---
      const blob = await generateImageBlob(prompt, model as any, options as any);

      // Replace Placeholder with Real Image
      const localUrl = URL.createObjectURL(blob);

      setGenerations(prev => prev.map(g => g.id === tempId ? {
        ...g,
        img: localUrl,
        isLoading: false
      } : g));

      // Upload in background
      const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([blob], `generation.${extension}`, { type: blob.type || 'image/jpeg' });
      const { uploadToR2 } = await import('../lib/storage');
      const publicUrl = await uploadToR2(file, `lookbook/${user.id}`, user.id);

      // Update with Public URL once uploaded
      setGenerations(prev => prev.map(g => g.id === tempId ? { ...g, img: publicUrl } : g));

      console.log('💾 Saving to Supabase (background)...');
      const sbUrl = (import.meta as any).env.VITE_SUPABASE_URL;
      const sbKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY;
      const projectRef = sbUrl?.split('//')[1]?.split('.')[0];
      const storedAuth = localStorage.getItem(`sb-${projectRef}-auth-token`);
      const token = storedAuth ? JSON.parse(storedAuth)?.access_token : null;

      if (token && sbUrl && sbKey) {
        fetch(`${sbUrl}/rest/v1/generations`, {
          method: 'POST',
          headers: {
            'apikey': sbKey,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify({
            user_id: user.id,
            prompt: displayPrompt,
            image_url: publicUrl,
            model: model,
            settings: {
              active_prompt: prompt,
              aspect_ratio: options?.aspectRatio,
              selections: options?.selections || {},
              characterReference: options?.character2DRef || undefined
            }
          })
        })
          .then(async resp => {
            if (resp.ok) {
              const data = await resp.json();
              if (data && data[0]?.id) {
                const newId = data[0].id;
                setGenerations(prev => prev.map(g => g.id === tempId ? { ...g, id: newId } : g));

                // Sync Layout ID to prevent stale reference (Black Image fix)
                setLayoutRows(prev => prev.map(row => row.map(item => {
                  if (item.type === 'single') {
                    return item.id === tempId ? { ...item, id: newId } : item;
                  } else {
                    return { ...item, ids: item.ids.map(mid => mid === tempId ? newId : mid) };
                  }
                })));
              }
              onGenerationComplete?.(publicUrl, displayPrompt);
            }
          })
          .catch(err => console.error('❌ Save error:', err));
      }

    } catch (error: any) {
      console.error('Generation Failed:', error);
      // Remove the failed generation from the canvas (only if tempId was assigned)
      if (tempId !== null) {
        setGenerations(prev => prev.filter(g => g.id !== tempId));
      }
      alert(`Generation failed: ${error.message || error}`);
    } finally {
      setIsGenerating(false);
    }
  };



  const handleRemove = (id: number | string) => {
    removeFromCanvas(id);

    if (selectedId === id) {
      setSelectedId(null);
      setExplicitClear(true);
      onClearForcedValues?.();
    }
  };

  // Keyboard Delete Handler
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if we have a selection and we are NOT typing in an input
      const hasSelection = selectedId !== null || selectedIds.size > 0;
      if (!hasSelection) return;

      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Remove all multi-selected items first
        if (selectedIds.size > 0) {
          selectedIds.forEach(id => handleRemove(id));
          setSelectedIds(new Set());
        }
        // Also remove single selected item if not already in multi-select
        if (selectedId !== null && !selectedIds.has(selectedId)) {
          handleRemove(selectedId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, selectedIds]);

  // Keyboard shortcuts for Lookbook/Gallery (Generator toggle, Gallery toggle)
  React.useEffect(() => {
    if (viewMode !== 'lookbook' && viewMode !== 'gallery') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      // Space - Toggle Generator Island
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        setIsGeneratorIslandOpen(prev => !prev);
      }

      // G - Toggle Gallery View
      if (e.key === 'g' || e.key === 'G') {
        setViewMode(prev => prev === 'gallery' ? 'lookbook' : 'gallery');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode]);

  const handleDelete = async (id: number | string, url?: string) => {
    handleRemove(id); // Optimistic Remove

    // Delete from database FIRST, then delete file (proper cleanup order)
    const isRecalled = String(id).startsWith('recalled-');
    if (!isRecalled) {
      try {
        const sbUrl = (import.meta as any).env.VITE_SUPABASE_URL;
        const sbKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY;
        const projectRef = sbUrl?.split('//')[1]?.split('.')[0];
        const storedAuth = localStorage.getItem(`sb-${projectRef}-auth-token`);
        const token = storedAuth ? JSON.parse(storedAuth)?.access_token : null;

        if (token && sbUrl && sbKey) {
          // Delete from generations table
          await fetch(`${sbUrl}/rest/v1/generations?id=eq.${id}`, {
            method: 'DELETE',
            headers: {
              'apikey': sbKey,
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          onDelete?.();
        }
      } catch (e) { console.error("❌ DB Delete failed", e); }
    }

    // Delete from project shots that reference this image
    if (url) {
      try {
        await ProjectService.deleteShotByImageUrl(url);
      } catch (e) {
        console.error("❌ Shot deletion error", e);
      }

      // Finally delete the actual file from R2 (after all DB references are gone)
      try {
        await deleteFromR2(url);
      } catch (e) {
        console.error("❌ R2 deletion error", e);
      }
    }
  };

  const handleEdit = (prompt: string, selections?: Record<string, any>, model?: string, img?: string) => {
    if (img) {
      setActiveEditorImage(img);
      setViewMode('editor');
    }
  };

  const { showToast } = useToast();
  const handleSendToChat = useSendToChat(globalProjectId);

  const galleryRef = React.useRef<HTMLDivElement>(null);

  // Scroll canvas to bottom ONLY when new images are added
  const isRestoringRef = React.useRef(false);

  const gridContainerRef = React.useRef<HTMLDivElement>(null);
  // Initialize to 1 to avoid TDZ dependency on viewportStateRef. 
  // It will be updated by onTransformed or manually synced if needed.
  const scaleRef = React.useRef<number>(1);
  const prevGenCountRef = React.useRef(generations.length);

  // Sync scaleRef with viewportStateRef on mount if available
  React.useEffect(() => {
    if (viewportStateRef.current?.scale) {
      scaleRef.current = viewportStateRef.current.scale;
    }
  }, []);

  const handleFitToView = React.useCallback(() => {
    if (!transformComponentRef.current) return;
    if (!transformComponentRef.current.instance || !transformComponentRef.current.instance.container) return; // Safety check

    if (generations.length === 0) {
      transformComponentRef.current.resetTransform();
      return;
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    generations.forEach(g => {
      const x = (g as any).x ?? 1500;
      const y = (g as any).y ?? 1500;
      const w = (g as any).width ?? 300;
      const h = (g as any).height ?? 300;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    });

    const padding = 200;
    const contentW = maxX - minX + (padding * 2);
    const contentH = maxY - minY + (padding * 2);
    const centerX = minX - padding + (contentW / 2);
    const centerY = minY - padding + (contentH / 2);

    // Use sidebar-aware viewport dimensions
    const viewport = getViewportDimensions();
    const clientW = transformComponentRef.current.instance.container.offsetWidth || viewport.width;
    const clientH = transformComponentRef.current.instance.container.offsetHeight || viewport.height;

    const scaleX = clientW / contentW;
    const scaleY = clientH / contentH;
    const fitScale = Math.min(Math.min(scaleX, scaleY), 1); // Cap at 100% zoom

    // Center logic: center of viewport - (center of content * scale)
    const newX = (clientW / 2) - (centerX * fitScale);
    const newY = (clientH / 2) - (centerY * fitScale);

    transformComponentRef.current.setTransform(newX, newY, fitScale, 500, "easeOut");
  }, [generations, getViewportDimensions]);

  // Keep ref in sync
  React.useEffect(() => {
    handleFitToViewRef.current = handleFitToView;
  }, [handleFitToView]);

  // Initial Fit on Mount or when switching to lookbook with content
  React.useEffect(() => {
    if (viewMode === 'lookbook' && generations.length > 0) {
      // We need to wait for the canvas to mount, then fit
      const attemptFit = (retries = 0) => {
        if (transformComponentRef.current && transformComponentRef.current.instance && transformComponentRef.current.instance.container) {
          handleFitToViewRef.current();
        } else if (retries < 10) {
          // Retry with increasing delays
          setTimeout(() => attemptFit(retries + 1), 100 * (retries + 1));
        }
      };
      // Small initial delay for render
      setTimeout(() => attemptFit(), 100);
    }
  }, [viewMode, generations.length]); // Trigger when viewMode or generations change

  React.useEffect(() => {
    if (scrollRef.current && viewMode === 'canvas') {
      // Only scroll if generations length increased (new content)
      // This prevents auto-scrolling to bottom during drag-and-drop reordering
      if (generations.length > prevGenCountRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }
    prevGenCountRef.current = generations.length;
  }, [generations.length, viewMode]);

  // Scroll gallery/storyboard to top when switching tabs
  React.useEffect(() => {
    if (galleryRef.current && (viewMode === 'gallery' || viewMode === 'storyboard')) {
      galleryRef.current.scrollTop = 0;
    }
  }, [viewMode]);

  const getRelativeWidth = (ratio?: string): number => {
    if (!ratio) return 1;
    const match = ratio.match(/(\d+):(\d+)/);
    if (!match) return 1;
    const w = parseFloat(match[1]);
    const h = parseFloat(match[2]);
    if (isNaN(w) || isNaN(h) || h === 0) return 1;
    return w / h;
  };

  const getItemAspectRatio = (id: string) => {
    const gen = generations.find(g => g.id === id);
    if (!gen) return 1;
    return getRelativeWidth(gen.settings?.aspect_ratio);
  };

  type LayoutItem =
    | { type: 'single'; id: string; flex?: number }
    | { type: 'stack'; ids: string[]; flex?: number; stackFlex?: number[] };

  // Initialize layoutRows from temp session if available
  const [layoutRows, setLayoutRows] = React.useState<LayoutItem[][]>(() => {
    const tempSession = loadTempSession();
    if (tempSession?.layout_data && Array.isArray(tempSession.layout_data) && tempSession.generations) {
      // Get valid generation IDs (those with actual image URLs)
      const validIds = new Set(
        tempSession.generations
          .filter((g: any) => g && g.img && typeof g.img === 'string' && g.img.length > 0)
          .map((g: any) => String(g.id))
      );

      if (validIds.size === 0) return [];

      // Filter layoutRows to only include valid IDs
      const filteredLayout = tempSession.layout_data
        .map((row: LayoutItem[]) =>
          row.map((item: LayoutItem) => {
            if (item.type === 'single') {
              return validIds.has(String(item.id)) ? item : null;
            } else {
              const validStackIds = item.ids.filter((id: string) => validIds.has(String(id)));
              if (validStackIds.length === 0) return null;
              if (validStackIds.length === 1) return { type: 'single', id: validStackIds[0] } as LayoutItem;
              return { ...item, ids: validStackIds };
            }
          }).filter(Boolean) as LayoutItem[]
        )
        .filter((row: LayoutItem[]) => row.length > 0);

      return filteredLayout;
    }
    return [];
  });

  // Track if we're restoring from a saved session (skip auto-layout)
  // const isRestoringRef = React.useRef(false); // Removed duplicate

  // Auto-save to localStorage when canvas changes (for refresh persistence)
  React.useEffect(() => {
    if (generations.length > 0) {
      const imageIds = generations.map(g => String(g.id));
      saveTempSession(layoutRows, imageIds, generations);
    } else {
      // If no images, we should still save the empty state or clear it 
      // so it doesn't restore old data on refresh
      clearTempSession();
    }
  }, [generations, layoutRows]);

  // AUTO-LAYOUT: Specific "Comic Logic" Score System
  const getLayoutScore = (ratioStr?: string) => {
    const ar = getRelativeWidth(ratioStr);
    // Tall (< 0.85) -> Score 1 (Fits 3)
    // Wide (> 1.15) -> Score 2 (Fits 1 + 1 Tall)
    // Square (0.85 - 1.15) -> Score 1.5 (Fits 2)
    if (ar < 0.85) return 1;
    if (ar > 1.15) return 2;
    return 1.5;
  };

  const MAX_ROW_SCORE = 3;

  React.useEffect(() => {
    // Skip auto-layout if we're in the middle of restoring a session
    // or if there are no generations to layout.
    if (isRestoringRef.current || generations.length === 0) {
      isRestoringRef.current = false; // Reset the flag
      return;
    }

    if (layoutRows.length === 0 && generations.length > 0) {
      const newRows: LayoutItem[][] = [];
      let currentRow: LayoutItem[] = [];
      let currentScore = 0;

      generations.forEach(g => {
        const itemScore = getLayoutScore(g.settings?.aspect_ratio);

        if ((currentScore + itemScore > MAX_ROW_SCORE) && currentRow.length > 0) {
          newRows.push(currentRow);
          currentRow = [];
          currentScore = 0;
        }
        currentRow.push({ type: 'single', id: g.id });
        currentScore += itemScore;
      });
      if (currentRow.length > 0) newRows.push(currentRow);
      setLayoutRows(newRows);
    } else {
      setLayoutRows(prev => {
        const validIds = new Set(generations.map(g => g.id));

        // 1. CLEANUP: Remove stale items
        let rows = prev.map(row => {
          return row.map(item => {
            if (item.type === 'single') {
              return validIds.has(item.id) ? item : null;
            } else {
              const newIds = item.ids.filter(id => validIds.has(id));
              if (newIds.length === 0) return null;
              if (newIds.length === 1) return { type: 'single', id: newIds[0] } as LayoutItem;
              // Reset stackFlex if count changes to avoid mismatch
              return { ...item, ids: newIds, stackFlex: item.ids.length === newIds.length ? item.stackFlex : undefined };
            }
          }).filter(Boolean) as LayoutItem[];
        }).filter(row => row.length > 0);

        // 2. ADD: Insert new items
        const existingIds = new Set<string>();
        rows.flat().forEach(item => {
          if (item.type === 'single') existingIds.add(item.id);
          else item.ids.forEach(id => existingIds.add(id));
        });

        const newImages = generations.filter(g => !existingIds.has(g.id));
        if (newImages.length === 0) return rows;

        // Append Logic
        let lastRow = rows.length > 0 ? [...rows[rows.length - 1]] : [];
        let lastRowScore = 0;

        lastRow.forEach(item => {
          if (item.type === 'single') lastRowScore += getLayoutScore(generations.find(g => g.id === item.id)?.settings?.aspect_ratio);
          else lastRowScore += 1;
        });

        const overflowImages: typeof generations = [];

        newImages.forEach(g => {
          const itemScore = getLayoutScore(g.settings?.aspect_ratio);
          if (lastRowScore + itemScore <= MAX_ROW_SCORE) {
            lastRow.push({ type: 'single', id: g.id });
            lastRowScore += itemScore;
          } else {
            overflowImages.push(g);
          }
        });

        if (rows.length > 0) rows[rows.length - 1] = lastRow;
        else if (lastRow.length > 0) rows.push(lastRow);

        let currentRow: LayoutItem[] = [];
        let currentScore = 0;
        overflowImages.forEach(g => {
          const itemScore = getLayoutScore(g.settings?.aspect_ratio);
          if ((currentScore + itemScore > MAX_ROW_SCORE) && currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
            currentScore = 0;
          }
          currentRow.push({ type: 'single', id: g.id });
          currentScore += itemScore;
        });
        if (currentRow.length > 0) rows.push(currentRow);

        return rows;
      });
    }
  }, [generations.length]);

  // RESIZING LOGIC
  const [resizing, setResizing] = React.useState<{
    type: 'row' | 'stack';
    rowIdx: number;
    colIdx: number; // For row: index of LEFT ITEM. For stack: index of TOP ITEM.
    stackItemIdx?: number;
    startPos: number; // ClientX or ClientY
    initialSizes: number[]; // Pixel sizes of items involved to prevent jumping
  } | null>(null);

  React.useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const delta = resizing.type === 'row'
        ? e.clientX - resizing.startPos
        : e.clientY - resizing.startPos;

      setLayoutRows(prev => {
        const newRows = [...prev];
        const row = [...newRows[resizing.rowIdx]];

        if (resizing.type === 'row') {
          const leftIdx = resizing.colIdx;
          const rightIdx = resizing.colIdx + 1;
          const leftItem = { ...row[leftIdx] };
          const rightItem = { ...row[rightIdx] };

          // Ensure initial sizes set bases
          const baseLeft = resizing.initialSizes[0];
          const baseRight = resizing.initialSizes[1];

          leftItem.flex = Math.max(50, baseLeft + delta);
          rightItem.flex = Math.max(50, baseRight - delta);

          row[leftIdx] = leftItem;
          row[rightIdx] = rightItem;
          newRows[resizing.rowIdx] = row;
        } else {
          // Stack Resize
          const item = { ...row[resizing.colIdx] };
          if (item.type === 'stack' && resizing.stackItemIdx !== undefined) {
            const topIdx = resizing.stackItemIdx;
            const bottomIdx = resizing.stackItemIdx + 1;

            const currentFlexes = item.stackFlex ? [...item.stackFlex] : item.ids.map(() => 100); // Default 100px basis if undefined

            // If this is first resize, initialSizes captured px heights
            const baseTop = resizing.initialSizes[0];
            const baseBottom = resizing.initialSizes[1];

            const newTop = Math.max(30, baseTop + delta);
            const newBottom = Math.max(30, baseBottom - delta);

            currentFlexes[topIdx] = newTop;
            currentFlexes[bottomIdx] = newBottom;

            row[resizing.colIdx] = { ...item, stackFlex: currentFlexes };
            newRows[resizing.rowIdx] = row;
          }
        }
        return newRows;
      });
    };

    const handleMouseUp = () => setResizing(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  const startResize = (e: React.MouseEvent, type: 'row' | 'stack', rIdx: number, cIdx: number, stackIdx?: number) => {
    e.preventDefault();
    e.stopPropagation();

    // Measure current sizes and FREEZE the layout to pixels to prevent collapse
    let initialSizes: number[] = [];

    if (type === 'row') {
      const rowDiv = scrollRef.current?.children[rIdx] as HTMLElement;
      if (rowDiv) {
        // Measure all items in the row
        const currentWidths = Array.from(rowDiv.children).map(el => (el as HTMLElement).offsetWidth);

        // Commit these pixel widths to state for ALL items in the row
        setLayoutRows(prev => {
          const newRows = [...prev];
          newRows[rIdx] = newRows[rIdx].map((item, idx) => ({
            ...item,
            flex: currentWidths[idx] || 100
          }));
          return newRows;
        });

        // Set initial sizes for the pair being resized
        if (currentWidths[cIdx] && currentWidths[cIdx + 1]) {
          initialSizes = [currentWidths[cIdx], currentWidths[cIdx + 1]];
        }
      }
    } else {
      // Stack Resize
      const rowDiv = scrollRef.current?.children[rIdx] as HTMLElement;
      // The stack component is the cIdx child of the row
      // We look for the stack container logic. 
      // Based on render: 
      // {item.type === 'stack' ? <div className="... flex flex-col ..."> 
      const stackWrapper = rowDiv?.children[cIdx] as HTMLElement;
      const stackContainer = stackWrapper?.querySelector('.flex-col') as HTMLElement; // Inner flex-col container?
      // Wait, render logic:
      // <div className="relative flex-1 ..." > 
      //    {isStack ? <div className="w-full h-full flex flex-col gap-4"> ... </div> : ... }
      // </div>

      const actualStackContainer = stackWrapper?.firstElementChild as HTMLElement; // The flex-col div

      if (actualStackContainer && stackIdx !== undefined) {
        const currentHeights = Array.from(actualStackContainer.children).map(el => (el as HTMLElement).offsetHeight);

        // Commit these pixel heights to state for the stack
        setLayoutRows(prev => {
          const newRows = [...prev];
          const stackItem = { ...newRows[rIdx][cIdx] };
          if (stackItem.type === 'stack') {
            stackItem.stackFlex = currentHeights;
            newRows[rIdx][cIdx] = stackItem;
          }
          return newRows;
        });

        if (currentHeights[stackIdx] && currentHeights[stackIdx + 1]) {
          initialSizes = [currentHeights[stackIdx], currentHeights[stackIdx + 1]];
        }
      }
    }

    if (initialSizes.length === 2) {
      setResizing({
        type,
        rowIdx: rIdx,
        colIdx: cIdx,
        stackItemIdx: stackIdx,
        startPos: type === 'row' ? e.clientX : e.clientY,
        initialSizes
      });
    }
  };

  // Check if dragged item is alone in the last row (to hide redundant bottom drop zone)
  const isDraggedAloneInLastRow = React.useMemo(() => {
    if (!draggedId || layoutRows.length === 0) return false;
    const lastRow = layoutRows[layoutRows.length - 1];
    if (lastRow.length !== 1) return false;
    const item = lastRow[0];
    if (item.type === 'single' && item.id === draggedId) return true;
    if (item.type === 'stack' && item.ids.length === 1 && item.ids[0] === draggedId) return true;
    return false;
  }, [draggedId, layoutRows]);

  const handleItemDragOver = React.useCallback((e: React.DragEvent, rIdx: number, cIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedId) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;

    const currentRow = layoutRows[rIdx];
    const isRowFull = currentRow && currentRow.length >= 5;

    let position: 'left' | 'right' | 'top' | 'bottom' | 'swap' = 'top';

    const sideThreshold = Math.min(w * 0.2, 80);
    const vertThreshold = Math.min(h * 0.2, 80);

    let isSide = false;
    // Allow side drop only if row not full
    if (!isRowFull) {
      if (x < sideThreshold) { position = 'left'; isSide = true; }
      else if (x > w - sideThreshold) { position = 'right'; isSide = true; }
    }

    if (!isSide) {
      if (y < vertThreshold) position = 'top';
      else if (y > h - vertThreshold) position = 'bottom';
      else {
        // Center Zone -> Try Swap
        let isDraftSingle = false;
        for (const row of layoutRows) {
          for (const item of row) {
            if (item.type === 'single' && item.id === draggedId) { isDraftSingle = true; break; }
          }
          if (isDraftSingle) break;
        }

        const targetItem = layoutRows[rIdx][cIdx];
        if (isDraftSingle && targetItem.type === 'single') {
          position = 'swap';
        } else {
          position = 'top';
        }
      }
    }

    setActiveDropZone(prev => {
      if (prev?.rowIdx === rIdx && prev?.colIdx === cIdx && prev?.position === position) return prev;
      return { rowIdx: rIdx, colIdx: cIdx, position: position as any };
    });
  }, [draggedId, layoutRows]);

  const handleDrop = () => {
    if (!draggedId || !activeDropZone) return;
    setLayoutRows(prev => {
      // Find source location to calculate index shifts
      let sourceRowIdx = -1;
      let sourceColIdx = -1;
      // We assume draggedId is unique enough or we take the first occurrence
      prev.forEach((row, r) => {
        row.forEach((item, c) => {
          if (sourceRowIdx !== -1) return; // already found
          if (item.type === 'single') {
            if (item.id === draggedId) { sourceRowIdx = r; sourceColIdx = c; }
          } else {
            if (item.ids.includes(draggedId as string)) { sourceRowIdx = r; sourceColIdx = c; }
          }
        });
      });

      // Handle Immediate Swap if drop zone is 'swap'
      if (activeDropZone.position === 'swap' && sourceRowIdx !== -1) {
        const newRows = prev.map(r => [...r]);
        const sRow = newRows[sourceRowIdx];
        const tRow = newRows[activeDropZone.rowIdx];

        if (activeDropZone.rowIdx === sourceRowIdx) {
          [sRow[sourceColIdx], sRow[activeDropZone.colIdx]] = [sRow[activeDropZone.colIdx], sRow[sourceColIdx]];
        } else {
          const temp = sRow[sourceColIdx];
          sRow[sourceColIdx] = tRow[activeDropZone.colIdx];
          tRow[activeDropZone.colIdx] = temp;
        }
        return newRows;
      }

      // Determine if source column/row will be removed
      let isSourceColumnRemoved = false;
      let isSourceRowRemoved = false;

      if (sourceRowIdx !== -1) {
        const sourceItem = prev[sourceRowIdx][sourceColIdx];
        if (sourceItem.type === 'single') {
          isSourceColumnRemoved = true;
        } else if (sourceItem.type === 'stack') {
          // If stack has only 1 item (the one being dragged), column is removed.
          // Or more robustly: count how many items will remain.
          const remainingIds = sourceItem.ids.filter(id => id !== draggedId);
          if (remainingIds.length === 0) isSourceColumnRemoved = true;
        }

        if (isSourceColumnRemoved && prev[sourceRowIdx].length === 1) {
          isSourceRowRemoved = true;
        }
      }

      // First, remove the dragged item from its current location
      const cleanRows = prev.map(row => {
        return row.map(item => {
          if (item.type === 'single') {
            return item.id === draggedId ? null : item;
          } else {
            const newIds = item.ids.filter(id => id !== draggedId);
            return newIds.length === 0 ? null : (newIds.length === 1 ? { type: 'single', id: newIds[0] } as LayoutItem : { ...item, ids: newIds, stackFlex: undefined });
          }
        }).filter(Boolean) as LayoutItem[];
      }).filter(row => row.length > 0);

      let { rowIdx, colIdx, position } = activeDropZone;

      // Adjust indices based on removal
      if (sourceRowIdx !== -1) {
        // If a row above the target (or the target row itself) was removed, shift target row up
        if (isSourceRowRemoved && sourceRowIdx < rowIdx) {
          rowIdx--;
        }

        // If we are dropping into the SAME row (after row adj), and valid source col was to the left, shift col left
        // Note: We use original row indices to compare identity. 
        // If sourceRowIdx === original_target_row_idx, we are in same row.
        // Wait, if row was removed vs not removed changes logic.

        // Scenario 1: Row was NOT removed. We allow col shift.
        if (!isSourceRowRemoved && sourceRowIdx === activeDropZone.rowIdx) {
          if (isSourceColumnRemoved && sourceColIdx < colIdx) {
            colIdx--;
          }
        }
        // Scenario 2: Row WAS removed. 
        // We are dropping into a "new" row at the same index? Or dropping into the next row which shifted up.
        // If I drag sole item A from Row 0. Drop into Row 0 (New).
        // source=0. target=0. sourceRowRemoved=true.
        // rowIdx (0) not adjusted (0 < 0 false).
        // target is 0. cleanRows[0] is empty/undefined (because row removed).
        // Logic below creates cleanRows[0]=[]. Correct.

        // Scenario 3: Drag sole item A from Row 0. Drop into Row 1.
        // source=0. target=1. sourceRowRemoved=true.
        // rowIdx becomes 0.
        // cleanRows[0] is old Row 1.
        // We drop into old Row 1. Correct.
      }

      const newItem: LayoutItem = { type: 'single', id: draggedId as string };

      // Handle new row creation
      if (position === 'new-row-top') {
        if (rowIdx <= 0) {
          // Insert at very top
          cleanRows.unshift([newItem]);
        } else {
          // Insert before the specified row (between-row drop)
          cleanRows.splice(rowIdx, 0, [newItem]);
        }
        return cleanRows;
      }
      if (position === 'new-row-bottom') {
        cleanRows.push([newItem]);
        return cleanRows;
      }

      if (!cleanRows[rowIdx]) cleanRows[rowIdx] = [];
      const targetRow = cleanRows[rowIdx];
      const targetItem = targetRow[colIdx] || null;

      if (!targetItem) {
        // Only add if row has less than 5 items
        if (targetRow.length < 5) {
          targetRow.push(newItem);
        }
      } else if (position === 'left' || position === 'right') {
        // Only add side column if row has less than 5 items
        if (targetRow.length < 5) {
          const idx = position === 'left' ? colIdx : colIdx + 1;
          targetRow.splice(idx, 0, newItem);
          // Reset flex for all items in this row to ensure they share space based on AR
          targetRow.forEach(item => { item.flex = undefined; });
        }
      } else {
        if (targetItem.type === 'single') {
          targetRow[colIdx] = {
            type: 'stack',
            ids: position === 'top' ? [newItem.id, targetItem.id] : [targetItem.id, newItem.id]
          };
        } else {
          const newIds = [...targetItem.ids];
          if (position === 'top') newIds.unshift(newItem.id);
          else newIds.push(newItem.id);
          targetRow[colIdx] = { ...targetItem, ids: newIds, stackFlex: undefined };
        }
      }
      return cleanRows;
    });
    setDraggedId(null);
    setActiveDropZone(null);
  };

  const resolvedForcedValues = React.useMemo(() => {
    if (explicitClear) return { clearInput: true };
    if (copiedPrompt) return { prompt: copiedPrompt, selections: copiedSelections, model: copiedModel };

    if (selectedId) {
      const g = generations.find(i => i.id === selectedId);
      if (g) {
        return {
          prompt: g.prompt,
          selections: g.settings?.selections || {},
          model: g.model,
          // Explicitly pass null when no character to clear previous selection
          characterReference: g.settings?.characterReference || null
        };
      }
    }

    if (propForcedValues) return propForcedValues;

    return undefined;
  }, [explicitClear, copiedPrompt, selectedId, generations, propForcedValues, copiedSelections, copiedModel]);

  // Convert generations to ViewerImage format for fullscreen viewer
  const viewerImages: ViewerImage[] = React.useMemo(() =>
    generations.map(gen => ({
      url: gen.img || '',
      label: gen.prompt?.substring(0, 50) + (gen.prompt?.length > 50 ? '...' : ''),
      id: gen.id,
      metadata: { model: gen.model, settings: gen.settings }
    })),
    [generations]
  );

  const hasContent = generations.length > 0 || isGenerating;


  return (
    <div className="w-full flex-1 min-h-0 bg-onyx rounded-none overflow-hidden relative z-20 group/console flex flex-col border-none" >
      {

      }

      {/* Feed Container */}


      {viewMode === 'lookbook' ? (
        isLoadingProject ? (
          <div className="flex-1 bg-zinc-900 overflow-hidden relative flex items-center justify-center">
            <div className="text-zinc-500 text-sm">Loading...</div>
          </div>
        ) : (
        <div
          className={`flex-1 bg-zinc-900 overflow-hidden relative ${middlePanning ? 'cursor-grabbing' : (canvasTool === 'pan' ? 'cursor-grab' : (canvasTool === 'text' ? 'cursor-text' : 'cursor-default'))}`}
          onPointerDown={(e) => {
            // Text Tool Placement
            if (canvasTool === 'text' && e.button === 0) {
              // We need to calculate the correct X/Y in the canvas coordinate system
              // The event originates from the container or text item. 
              // Since this handler is on the WRAPPER (outer div), e.nativeEvent.offsetX/Y gives mouse pos relative to wrapper.
              // We need to transform this to canvas coordinates.

              if (transformComponentRef.current) {
                const { positionX, positionY, scale } = transformComponentRef.current.instance.transformState;
                const rect = e.currentTarget.getBoundingClientRect();
                const pX = e.clientX - rect.left;
                const pY = e.clientY - rect.top;

                const canvasX = (pX - positionX) / scale;
                const canvasY = (pY - positionY) / scale;

                placeTextItem(canvasX, canvasY);
                return;
              }
            }

            // Middle Mouse Panning (Button 1)
            if (e.button === 1) {
              e.preventDefault();
              setMiddlePanning(true);
              lastMousePos.current = { x: e.clientX, y: e.clientY };
              (e.currentTarget as any).setPointerCapture(e.pointerId);
              return;
            }
          }}
          onPointerMove={(e) => {
            if (middlePanning && transformComponentRef.current) {
              const dx = e.clientX - lastMousePos.current.x;
              const dy = e.clientY - lastMousePos.current.y;

              const { positionX, positionY, scale } = transformComponentRef.current.instance.transformState;
              transformComponentRef.current.setTransform(positionX + dx, positionY + dy, scale, 0);

              lastMousePos.current = { x: e.clientX, y: e.clientY };
            }
          }}
          onPointerUp={(e) => {
            if (e.button === 1) {
              setMiddlePanning(false);
              (e.currentTarget as any).releasePointerCapture(e.pointerId);
            }
          }}
          ref={gridContainerRef}
          style={{
            backgroundImage: 'radial-gradient(#333 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            backgroundPosition: '0px 0px'
          }}
        >
          <TransformWrapper
            ref={transformComponentRef}
            initialScale={viewportStateRef.current?.scale ?? 1}
            initialPositionX={viewportStateRef.current?.positionX ?? 0}
            initialPositionY={viewportStateRef.current?.positionY ?? 0}
            minScale={0.1}
            maxScale={10}
            limitToBounds={false}
            wheel={{ step: 0.08, excluded: ['stop-wheel'] }}
            smooth={true}
            alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
            velocityAnimation={{ sensitivity: 1, animationTime: 200, animationType: 'easeOutQuad' }}
            panning={{ disabled: canvasTool !== 'pan', velocityDisabled: false }}
            doubleClick={{ disabled: true }}
            onInit={() => {
              // Only fit to content on first load (no saved state and not loading from project)
              if (!viewportStateRef.current && !isLoadingProject) {
                setTimeout(() => handleFitToViewRef.current(), 50);
              }
            }}
            onTransformed={(r) => {
              const { positionX, positionY, scale } = r.instance.transformState;

              // Save state for persistence
              viewportStateRef.current = { positionX, positionY, scale };

              // Trigger debounced Supabase save
              setViewportVersion(v => v + 1);

              if (gridContainerRef.current) {
                gridContainerRef.current.style.backgroundSize = `${24 * scale}px ${24 * scale}px`;
                gridContainerRef.current.style.backgroundPosition = `${positionX}px ${positionY}px`;
                scaleRef.current = scale;
              } else {
                scaleRef.current = scale;
              }
            }}
          >
            {({ zoomIn, zoomOut, resetTransform, zoomToElement, setTransform, state }) => {
              // Capture controls for external use
              viewportControlRef.current = { zoomToElement, setTransform, resetTransform };

              return (
                <>
                  {/* Floating Toolbar (Left) */}
                  <div className="absolute left-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 p-2 bg-zinc-900/80 backdrop-blur-md border border-white/10 rounded-2xl shadow-xl">
                    <button
                      onClick={() => setCanvasTool('select')}
                      className={`p-3 rounded-xl transition-all ${canvasTool === 'select' ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 scale-105' : 'text-zinc-400 hover:text-white hover:bg-white/10'}`}
                      title="Select Tool"
                    >
                      <CursorIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCanvasTool('pan')}
                      className={`p-3 rounded-xl transition-all ${canvasTool === 'pan' ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 scale-105' : 'text-zinc-400 hover:text-white hover:bg-white/10'}`}
                      title="Pan Tool"
                    >
                      <HandIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCanvasTool('text')}
                      className={`p-3 rounded-xl transition-all ${canvasTool === 'text' ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 scale-105' : 'text-zinc-400 hover:text-white hover:bg-white/10'}`}
                      title="Text Tool"
                    >
                      <TextTIcon className="w-5 h-5" />
                    </button>

                    <div className="h-px w-8 bg-white/10 mx-auto my-1" />

                    <button onClick={() => zoomIn()} className="p-3 text-zinc-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors" title="Zoom In">
                      <PlusIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => zoomOut()} className="p-3 text-zinc-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors" title="Zoom Out">
                      <MinusIcon className="w-5 h-5" />
                    </button>

                    <div className="h-px w-8 bg-white/10 mx-auto my-1" />

                    {/* Borders Toggle */}
                    <button
                      onClick={() => {
                        const next = lookbookBorder === 'thick' ? 'thin' : (lookbookBorder === 'thin' ? 'none' : 'thick');
                        setLookbookBorder(next);
                      }}
                      className={`p-3 rounded-xl transition-all ${lookbookBorder !== 'none' ? 'text-indigo-400' : 'text-zinc-500'} hover:text-white hover:bg-white/10`}
                      title={`Border Style: ${lookbookBorder}`}
                    >
                      <FrameCornersIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <TransformComponent
                    wrapperClass={`w-full h-full ${canvasTool === 'pan' ? 'cursor-grab active:cursor-grabbing' : 'select-none'}`}
                    contentClass="w-full h-full"
                    wrapperStyle={{ width: "100%", height: "100%" }}
                  >
                    <div
                      ref={scrollRef}
                      className="relative min-w-[25000px] min-h-[25000px]"
                      onPointerDown={(e) => {
                        // Only start marquee on left click directly on canvas background
                        if (e.button !== 0) return;
                        if (canvasTool !== 'select') return;

                        // Capture pointer for marquee drag
                        (e.target as HTMLElement).setPointerCapture(e.pointerId);

                        // Get canvas coordinates accounting for zoom/pan
                        // Use the outer wrapper for reference, not the transformed content
                        const wrapperRect = gridContainerRef.current?.getBoundingClientRect();
                        if (!wrapperRect) return;
                        const scale = state?.scale ?? 1;
                        const posX = state?.positionX ?? 0;
                        const posY = state?.positionY ?? 0;
                        // Convert screen coords to canvas coords
                        const canvasX = (e.clientX - wrapperRect.left - posX) / scale;
                        const canvasY = (e.clientY - wrapperRect.top - posY) / scale;

                        // Clear existing selection
                        setSelectedId(null);
                        setSelectedIds(new Set());
                        setExplicitClear(true);

                        // Start marquee selection
                        setMarqueeStart({ x: canvasX, y: canvasY });
                        setMarqueeEnd({ x: canvasX, y: canvasY });
                      }}
                      onPointerMove={(e) => {
                        if (!marqueeStart) return;

                        const wrapperRect = gridContainerRef.current?.getBoundingClientRect();
                        if (!wrapperRect) return;
                        const scale = state?.scale ?? 1;
                        const posX = state?.positionX ?? 0;
                        const posY = state?.positionY ?? 0;
                        const canvasX = (e.clientX - wrapperRect.left - posX) / scale;
                        const canvasY = (e.clientY - wrapperRect.top - posY) / scale;

                        setMarqueeEnd({ x: canvasX, y: canvasY });
                      }}
                      onPointerUp={(e) => {
                        // Release pointer capture
                        (e.target as HTMLElement).releasePointerCapture(e.pointerId);

                        if (!marqueeStart || !marqueeEnd) {
                          setMarqueeStart(null);
                          setMarqueeEnd(null);
                          return;
                        }

                        // Calculate marquee bounds
                        const minX = Math.min(marqueeStart.x, marqueeEnd.x);
                        const maxX = Math.max(marqueeStart.x, marqueeEnd.x);
                        const minY = Math.min(marqueeStart.y, marqueeEnd.y);
                        const maxY = Math.max(marqueeStart.y, marqueeEnd.y);

                        // Only select if marquee has some size (not just a click)
                        if (maxX - minX > 10 || maxY - minY > 10) {
                          // Find all items that intersect with marquee
                          const selected = new Set<string | number>();
                          generations.forEach(item => {
                            const itemX = (item as any).x ?? 1500;
                            const itemY = (item as any).y ?? 1500;
                            const itemW = (item as any).width ?? 300;
                            const itemH = (item as any).height ?? 300;

                            // Check if item intersects marquee rectangle
                            const intersects = !(
                              itemX + itemW < minX ||
                              itemX > maxX ||
                              itemY + itemH < minY ||
                              itemY > maxY
                            );

                            if (intersects) {
                              selected.add(item.id);
                            }
                          });

                          if (selected.size > 0) {
                            setSelectedIds(selected);
                          }
                        }

                        setMarqueeStart(null);
                        setMarqueeEnd(null);
                      }}
                      onPointerCancel={(e) => {
                        // Handle cancelled pointer (e.g., touch interrupted)
                        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
                        setMarqueeStart(null);
                        setMarqueeEnd(null);
                      }}
                    >
                      {generations.map((item) => {
                        const x = (item as any).x ?? 1500;
                        const y = (item as any).y ?? 1500;

                        return (
                          <div key={item.id} id={`canvas-item-${item.id}`} className="absolute top-0 left-0 w-0 h-0">
                            <CanvasItem
                              item={{ ...item, x, y }}
                              isSelected={selectedId === item.id || selectedIds.has(item.id)}
                              onSelect={(shiftKey: boolean) => {
                                if (shiftKey) {
                                  // Shift+click: toggle item in multi-select set (don't populate generator)
                                  setSelectedIds(prev => {
                                    const next = new Set(prev);
                                    // If there's a single-selected item not in the set, add it first
                                    if (selectedId !== null && !next.has(selectedId)) {
                                      next.add(selectedId);
                                    }
                                    // Toggle the clicked item
                                    if (next.has(item.id)) {
                                      next.delete(item.id);
                                    } else {
                                      next.add(item.id);
                                    }
                                    return next;
                                  });
                                  // Clear single selection since we're now in multi-select mode
                                  setSelectedId(null);
                                } else {
                                  // Normal click: single select, clear multi-select
                                  setSelectedId(item.id);
                                  setSelectedIds(new Set());
                                  setExplicitClear(false);
                                }
                              }}
                              zoom={state?.scale ?? 1}
                              scaleRef={scaleRef}
                              canvasTool={canvasTool}
                              lookbookBorder={lookbookBorder}
                              selectedIds={selectedIds}
                              onGroupMove={(deltaX: number, deltaY: number) => {
                                // Move all OTHER selected items by the delta
                                setGenerations(prev => prev.map(g => {
                                  if (g.id !== item.id && selectedIds.has(g.id)) {
                                    return {
                                      ...g,
                                      x: ((g as any).x ?? 1500) + deltaX,
                                      y: ((g as any).y ?? 1500) + deltaY
                                    };
                                  }
                                  return g;
                                }));
                              }}
                              onUpdate={canEdit ? (id, updates) => {
                                setGenerations(prev => prev.map(g =>
                                  g.id === id ? { ...g, ...updates } : g
                                ));
                              } : undefined}
                              onDelete={canEdit ? handleDelete : undefined}
                              onRemove={canEdit ? handleRemove : undefined}
                              onTogglePublic={handleTogglePublic}
                              onToggleFavorite={handleToggleFavorite}
                              onViewFullscreen={() => {
                                const idx = generations.findIndex(g => g.id === item.id);
                                if (idx !== -1) setFullscreenImageIndex(idx);
                              }}
                              onAddToProject={canEdit ? (img, prompt) => handleAddToProject(img, prompt) : undefined}
                              onSendToChat={handleSendToChat}
                              isInProject={projectImageUrls.has(item.img)}
                              onInpaintComplete={(id, newImageUrl) => {
                                setGenerations(prev => prev.map(g =>
                                  g.id === id ? { ...g, img: newImageUrl } : g
                                ));
                              }}
                              onLoadError={(id) => {
                                // Remove image that failed to load (deleted or missing from R2)
                                console.warn(`Removing failed image from lookbook: ${id}`);
                                setGenerations(prev => prev.filter(g => g.id !== id));
                              }}
                              otherItems={generations.filter(g => String(g.id) !== String(item.id)).map(g => ({
                                id: g.id,
                                x: (g as any).x ?? 1500,
                                y: (g as any).y ?? 1500,
                                width: (g as any).width ?? 300,
                                height: (g as any).height ?? 300
                              }))}
                            />
                          </div>
                        );
                      })}

                      {/* Marquee selection rectangle */}
                      {marqueeStart && marqueeEnd && (
                        <div
                          className="absolute pointer-events-none border-2 border-indigo-500 bg-indigo-500/10"
                          style={{
                            left: Math.min(marqueeStart.x, marqueeEnd.x),
                            top: Math.min(marqueeStart.y, marqueeEnd.y),
                            width: Math.abs(marqueeEnd.x - marqueeStart.x),
                            height: Math.abs(marqueeEnd.y - marqueeStart.y),
                          }}
                        />
                      )}
                    </div>
                  </TransformComponent>
                </>
              );
            }}
          </TransformWrapper >

          {/* Text Properties Panel */}
          {selectedId && (() => {
            const item = generations.find(g => g.id === selectedId);
            if (item?.type === 'text') {
              return (
                <div className="absolute top-24 right-8 z-[60] bg-zinc-950 border border-white/10 rounded-xl p-4 shadow-2xl w-64 animate-in slide-in-from-right-4 fade-in duration-200" onPointerDown={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold text-sm">Text Options</h3>
                    <button onClick={() => { setSelectedId(null); setExplicitClear(true); }}><XIcon className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
                  </div>

                  {/* Bubble Style */}
                  <div className="mb-4">
                    <label className="text-xs text-zinc-500 font-bold mb-2 block">BUBBLE STYLE</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['speech', 'thought', 'shout', 'rectangle', 'none'].map(s => (
                        <button
                          key={s}
                          onClick={() => {
                            setGenerations(prev => prev.map(g => g.id === item.id ? { ...g, bubble_style: s } : g));
                          }}
                          className={`px-2 py-1.5 rounded border flex items-center justify-center text-xs transition-colors ${item.bubble_style === s ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-900'}`}
                        >
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Font */}
                  <div className="mb-4">
                    <label className="text-xs text-zinc-500 font-bold mb-2 block">FONT</label>
                    <select
                      value={item.font_family}
                      onChange={(e) => setGenerations(prev => prev.map(g => g.id === item.id ? { ...g, font_family: e.target.value } : g))}
                      className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 appearance-none cursor-pointer"
                    >
                      <option value="Inter, sans-serif">Standard (Inter)</option>
                      <option value="'Bangers', cursive">Loud (Bangers)</option>
                      <option value="'Comic Neue', cursive">Comic (Comic Neue)</option>
                      <option value="'Permanent Marker', cursive">Marker</option>
                      <option value="'Lychee', sans-serif">Lychee (Custom)</option>
                    </select>
                  </div>

                  {/* Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-zinc-500 font-bold mb-2 block">TEXT</label>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full border border-white/10 overflow-hidden relative">
                          <input
                            type="color"
                            value={item.text_color}
                            onChange={(e) => setGenerations(prev => prev.map(g => g.id === item.id ? { ...g, text_color: e.target.value } : g))}
                            className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] cursor-pointer p-0 m-0"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 font-bold mb-2 block">BUBBLE</label>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full border border-white/10 overflow-hidden relative">
                          <input
                            type="color"
                            value={item.bubble_color}
                            onChange={(e) => setGenerations(prev => prev.map(g => g.id === item.id ? { ...g, bubble_color: e.target.value } : g))}
                            className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] cursor-pointer p-0 m-0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}

        </div >
        )
      ) : viewMode === 'gallery' ? (
        <div ref={galleryRef} className="relative h-0 flex-1 overflow-y-auto bg-carbon px-6 pb-4 pt-4">
          {recentGenerationsContent || (
            <div className="h-full flex items-center justify-center text-zinc-500">
              <div className="text-center p-8 border border-zinc-800 rounded-xl bg-zinc-900/50">
                <h3 className="text-xl font-bold text-white mb-2">Gallery</h3>
                <p className="max-w-md mx-auto text-sm">Your history of creations will appear here.</p>
              </div>
            </div>
          )}
        </div>

      ) : viewMode === 'storyboard' ? ( // Re-ordered else-if for clarity
        <div ref={galleryRef} className="h-0 flex-1 overflow-y-auto bg-carbon pb-4 pt-32">
          {storyboardContent || (
            <div className="h-full flex items-center justify-center text-zinc-500">
              <div className="text-center p-8 border border-zinc-800 rounded-xl bg-zinc-900/50">
                <h3 className="text-xl font-bold text-white mb-2">Storyboard</h3>
                <p className="max-w-md mx-auto text-sm">Organize your shots into sequences.</p>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Lookbook/Gallery Toolbar & Floating Generator */}
      {(viewMode === 'lookbook' || viewMode === 'gallery') && (
        <LookbookToolbar
          isGeneratorOpen={isGeneratorIslandOpen}
          onGeneratorToggle={() => setIsGeneratorIslandOpen(prev => !prev)}
          isGalleryOpen={viewMode === 'gallery'}
          onGalleryToggle={() => setViewMode(prev => prev === 'gallery' ? 'lookbook' : 'gallery')}
          showGenerator={canEdit}
        />
      )}

      {/* Floating Dynamic Island Generator - Visible in lookbook & gallery views (only for editors) */}
      {canEdit && (viewMode === 'lookbook' || viewMode === 'gallery') && (
        <DynamicIslandGenerator
          isOpen={isGeneratorIslandOpen}
          onClose={() => setIsGeneratorIslandOpen(false)}
          onMinimize={() => setIsGeneratorIslandOpen(false)}
        >
          <div className="p-4">
            <GeneratorInput
              variant="floating"
              activeTool={activeTool}
              isGenerating={isGenerating}
              forcedValues={resolvedForcedValues}
              onGenerate={handleGenerate}
              onInputChange={() => {
                if (selectedId !== null) setSelectedId(null);
                setExplicitClear(false);
              }}
              onStateChange={setToolState}
              onToolChange={setActiveTool}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </div>
        </DynamicIslandGenerator>
      )}

      {/* Bottom Docked Generator - Only for storyboard view (editors only) */}
      {canEdit && viewMode === 'storyboard' && (
        <div className="w-full shrink-0 z-50 flex flex-col items-center bg-onyx border-t border-zinc-800">
          {(activeTool === 'prompt' || activeTool === 'builder' || activeTool === 'upload') && (
            <div className="w-full max-w-7xl mx-auto p-4 pb-0">
              <GeneratorInput
                variant="docked"
                activeTool={activeTool}
                isGenerating={isGenerating}
                forcedValues={resolvedForcedValues}
                onGenerate={handleGenerate}
                onInputChange={() => {
                  if (selectedId !== null) setSelectedId(null);
                  setExplicitClear(false);
                }}
                onStateChange={setToolState}
                onToolChange={setActiveTool}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </div>
          )}
        </div>
      )}

      {/* Fullscreen Image Viewer for Lookbook */}
      <FullscreenImageViewer
        images={viewerImages}
        currentIndex={fullscreenImageIndex}
        onClose={() => setFullscreenImageIndex(null)}
        onNavigate={setFullscreenImageIndex}
        respectSidebars={true}
        onDownload={(image, index) => {
          const gen = generations[index];
          if (!gen || !gen.img) return;
          const link = document.createElement('a');
          link.href = gen.img;
          link.download = `generation-${gen.id}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
        onEdit={(image, index) => {
          // Close fullscreen - user can use inpainting on the canvas
          setFullscreenImageIndex(null);
        }}
        supportsInpainting={(image, index) => {
          const gen = generations[index];
          const model = gen?.model || '';
          return model.includes('z-image') || model.includes('nano-banana') || model.includes('turbo') || model === DEFAULT_IMAGE_MODEL;
        }}
      />

      {/* Toast Notification - centered between sidebars (left: 192px, right: 400px) */}
      {
        notification && (
          <div
            className="fixed top-24 z-[200] animate-in fade-in slide-in-from-top-4 duration-300 -translate-x-1/2"
            style={{ left: 'calc(192px + (100% - 192px - 400px) / 2)' }}
          >
            <div className={`
             flex items-center gap-3 px-4 py-2.5 rounded-lg border shadow-xl backdrop-blur-md
             ${notification.type === 'error'
                ? 'bg-red-500/10 border-red-500/20 text-red-200'
                : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-200'}
           `}>
              {notification.type === 'error' ? <XIcon className="w-4 h-4" /> : <CheckIcon className="w-4 h-4" />}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          </div>
        )
      }
    </div >
  );
}

// ... Additional Components ...
const recentProjects = [
  { id: 1, title: "The Martian Script", type: "Script", date: "2h ago", status: "In Progress", icon: FileTextIcon },
  { id: 2, title: "Cyberpunk Character Study", type: "Character", date: "5h ago", status: "Ready", icon: UserIcon },
  { id: 3, title: "Neon City Scene 01", type: "Scene", date: "1d ago", status: "Draft", icon: LayoutIcon },
  { id: 4, title: "Commercial Pitch", type: "Video", date: "2d ago", status: "Rendering", icon: FilmSlateIcon },
];
const quickActions = [
  { label: "New Script", icon: PenNibIcon, color: "text-blue-400", nav: "story" },
  { label: "Create Character", icon: UserIcon, color: "text-purple-400", nav: "character" },
  { label: "Lookbook", icon: FilmSlateIcon, color: "text-indigo-400", nav: "lookbook" },
  { label: "Render Video", icon: VideoIcon, color: "text-emerald-400", nav: "video" },
];
const quotes = [
  "Creativity is intelligence having fun.",
  "Every artist was first an amateur.",
  "The best way to predict the future is to create it.",
  "Art is not what you see, but what you make others see."
];

export const HeroDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [greeting, setGreeting] = React.useState('');
  const [quote] = React.useState(() => quotes[Math.floor(Math.random() * quotes.length)]);

  const [recentItems, setRecentItems] = React.useState<any[]>([]);
  const [topCreators, setTopCreators] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 18) setGreeting("Good afternoon");
    else setGreeting("Good evening");

    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // 1. Fetch recent generations
      const { data: recents } = await supabase
        .from('generations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);

      // 2. Fetch recent characters
      const { data: characters } = await supabase
        .from('characters')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(3);

      // 3. Fetch recent projects
      const { data: projects } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(3);

      // Merge and Sort
      const mixedItems: any[] = [];

      if (recents) {
        mixedItems.push(...recents.map(g => ({
          id: g.id,
          type: 'generation',
          title: g.prompt,
          subtitle: g.model || 'Image',
          img: g.image_url || g.img, // Handle likely mapping
          date: new Date(g.created_at),
          raw: g
        })));
      }

      if (characters) {
        mixedItems.push(...characters.map(c => ({
          id: c.id,
          type: 'character',
          title: c.name,
          subtitle: c.species ? `${c.species} Character` : 'Character',
          img: c.snapshot_url,
          date: new Date(c.updated_at),
          raw: c
        })));
      }

      if (projects) {
        mixedItems.push(...projects.map(p => ({
          id: p.id,
          type: 'project',
          title: p.title,
          subtitle: 'Project',
          img: null, // Use icon
          date: new Date(p.updated_at),
          raw: p
        })));
      }

      // Sort by date desc
      mixedItems.sort((a, b) => b.date.getTime() - a.date.getTime());

      // Take top 5
      setRecentItems(mixedItems.slice(0, 5));

      // Fetch Top Creators (by followers)
      const { data: creators } = await supabase
        .from('profiles')
        .select('*')
        .order('followers_count', { ascending: false })
        .limit(3);

      if (creators) setTopCreators(creators);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto pt-24 px-8 pb-20 bg-onyx text-neutral-200">
      <div className="max-w-[1000px] mx-auto">
        <div className="mb-16 animate-in fade-in slide-in-from-bottom-2 duration-700">
          <div className="flex items-center gap-3 mb-6 text-zinc-500 text-sm font-medium">
            <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
          </div>
          <h1 className="text-5xl font-heading font-semibold text-white mb-4 tracking-tight">
            {greeting}, {user?.name || 'Creator'}.
          </h1>
          <p className="text-zinc-500 text-lg font-serif italic max-w-xl opacity-80">"{quote}"</p>
        </div>
        <div className="mb-12">
          <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <PlusIcon className="w-3 h-3" /> Quick Create
          </h3>
          <div className="flex flex-wrap gap-4">
            {quickActions.map((action, i) => (
              <button
                key={i}
                onClick={() => navigate(`/${action.nav}`)}
                className="flex items-center gap-3 px-4 py-3 bg-onyx hover:bg-zinc-900 border border-white/5 hover:border-white/10 rounded-lg transition-all group"
              >
                <div className={`${action.color} opacity-80 group-hover:opacity-100`}>
                  <action.icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-zinc-300 group-hover:text-white">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-4 border-b border-white/5 pb-2">
            <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-2">
              <ClockIcon className="w-3 h-3" /> Jump Back In
            </h3>
            <button className="text-xs text-zinc-600 hover:text-zinc-400 font-medium transition-colors">View All</button>
          </div>
          <div className="flex flex-col gap-1">
            {recentItems.length === 0 ? (
              <div className="p-4 text-center text-zinc-500 text-sm border border-zinc-800/50 rounded-lg border-dashed">
                No recent activity. Start creating!
              </div>
            ) : (
              recentItems.map((item, i) => (
                <div
                  key={`${item.type}-${item.id}`}
                  className="group flex items-center justify-between p-3 rounded-lg hover:bg-onyx cursor-pointer transition-colors border border-transparent hover:border-white/5"
                  onClick={() => {
                    // Navigate based on type
                    if (item.type === 'project') navigate('/projects');
                    if (item.type === 'character') navigate('/character');
                    if (item.type === 'generation') navigate('/lookbook'); // Or open detail
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-white/5 flex items-center justify-center overflow-hidden shrink-0">
                      {item.img ? (
                        <img src={item.img} className="w-full h-full object-cover" />
                      ) : (
                        item.type === 'project' ? <FileTextIcon className="w-5 h-5 text-indigo-400" /> :
                          item.type === 'character' ? <UserIcon className="w-5 h-5 text-emerald-400" /> :
                            <LayoutIcon className="w-4 h-4 text-zinc-500" />
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-zinc-200 group-hover:text-white transition-colors truncate max-w-[200px]">{item.title || 'Untitled'}</h4>
                      <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                        <span className="uppercase tracking-wider">{item.subtitle}</span>
                        <span className="w-1 h-1 rounded-full bg-zinc-700" />
                        <span>{item.date.toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-white/5`}>
                      Ready
                    </span>
                    <button className="text-zinc-600 hover:text-white opacity-0 group-hover:opacity-100 transition-all">
                      <DotsThreeIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top Creators Section */}
        <div className="mt-12">
          <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2">
            <SparkleIcon className="w-3 h-3" /> Top Creators
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topCreators.map(creator => (
              <div key={creator.id} className="p-4 rounded-xl bg-onyx border border-white/5 hover:border-white/10 transition-colors flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-zinc-800 border-2 border-onyx overflow-hidden">
                  {creator.avatar_url ? <img src={creator.avatar_url} className="w-full h-full object-cover" /> : <UserIcon className="w-6 h-6 m-3 text-zinc-500" />}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white">@{creator.username || 'Creator'}</h4>
                  <p className="text-xs text-zinc-500">Visual Arist</p>
                </div>
                <button className="ml-auto px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-medium text-white transition-colors">
                  Follow
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/5">
          <div className="flex flex-col gap-4 text-zinc-500 text-sm">
            <div className="flex items-center gap-2 hover:text-white w-max cursor-pointer transition-colors">
              <ListBulletsIcon className="w-4 h-4" />
              <span>Review script notes for "The Martian"</span>
            </div>
            <div className="flex items-center gap-2 hover:text-white w-max cursor-pointer transition-colors">
              <MagnifyingGlassIcon className="w-4 h-4" />
              <span>Search for inspiration references...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
